// src/calcEngine.js
// JBF Retirement Planner — Version 2 Calculation Engine
// Steps 1–6, 9–10

import { snMaturityBumps } from './lib/snPayoff';

export const INFLATION = 0.0275;

// ── Stage B: LTCG / QDI preferential brackets + NIIT (2026 verified, status-keyed) ──
// Preferential income (long-term capital gains + qualified dividends) is taxed 0/15/20%
// by where it lands ON TOP of ordinary taxable income — ordinary consumes the lower
// brackets first. Each entry is [top-of-band taxable income, rate], keyed by filing
// status. 2026 figures (not further indexed here).
const LTCG_BREAKPOINTS = {
  single: [[49450, 0], [545500, 0.15], [Infinity, 0.20]],
  mfj:    [[98900, 0], [613700, 0.15], [Infinity, 0.20]],
};
// Net Investment Income Tax — flat 3.8% on min(net investment income, MAGI − threshold).
// Thresholds are statutory and NOT indexed.
export const NIIT_RATE = 0.038;
const NIIT_THRESHOLD = { single: 200000, mfj: 250000 };
const fsKey = (fs) => (fs === 'mfj' ? 'mfj' : 'single');

// Federal tax on preferential income stacked above ordinary taxable income. Each
// preferential dollar takes the 0/15/20 rate for where (ordinaryTaxable + that dollar)
// falls against the filing-status breakpoints.
export function preferentialFedTax(ordinaryTaxable, preferential, filingStatus) {
  if (!(preferential > 0)) return 0;
  let floor = Math.max(0, ordinaryTaxable);
  let remaining = preferential;
  let tax = 0;
  for (const [ceiling, rate] of LTCG_BREAKPOINTS[fsKey(filingStatus)]) {
    if (remaining <= 0) break;
    const room = Math.max(0, ceiling - floor);
    const amt = Math.min(remaining, room);
    tax += amt * rate;
    remaining -= amt;
    floor += amt;
  }
  return tax;
}

// NIIT = 3.8% × min(net investment income, MAGI − threshold). Filing-status-keyed.
export function niitTax(netInvestmentIncome, magi, filingStatus) {
  const over = Math.max(0, magi - NIIT_THRESHOLD[fsKey(filingStatus)]);
  return NIIT_RATE * Math.min(Math.max(0, netInvestmentIncome), over);
}

// Sum the income rows active at `age`. rowOrdinary (ordinary + earned) stacks as ordinary
// income; rowPreferential (LTCG + QDI) takes 0/15/20; rowNII is the net-investment-income
// subset for NIIT = preferential + ORDINARY-treated rows (dividends/interest/rents/STCG),
// EXCLUDING earned/wages. SS + pension stay on their dedicated engine inputs.
export function sumIncomeRows(incomeRows, age) {
  let rowOrdinary = 0, rowPreferential = 0, rowNII = 0;
  if (Array.isArray(incomeRows)) {
    for (const r of incomeRows) {
      const amt = Number(r.amount) || 0;
      if (amt <= 0) continue;
      const s = Number(r.startAge), e = Number(r.endAge);
      if (!Number.isFinite(s) || !Number.isFinite(e) || age < s || age > e) continue;
      if (r.treatment === 'preferential') { rowPreferential += amt; rowNII += amt; }
      else if (r.treatment === 'earned')  { rowOrdinary += amt; }               // wages — not NII
      else                                { rowOrdinary += amt; rowNII += amt; } // ordinary investment income
    }
  }
  return { rowOrdinary, rowPreferential, rowNII };
}

// Coerce a form's income rows into the numeric shape the engines consume. Returns []
// when the form has no incomeRows array — callers then fall back to the legacy
// preRetirementIncome earned-income path so old cases compute exactly as before.
export function resolveIncomeRows(form) {
  if (!form || !Array.isArray(form.incomeRows)) return [];
  return form.incomeRows.map(r => {
    const amount = parseFloat(r.amount) || 0;
    let startAge = parseFloat(r.startAge);
    let endAge   = parseFloat(r.endAge);
    // NEVER silently drop entered income because an age is left blank — that under-counts
    // the tax stack (the reported baseline bug). A blank start applies from age 0; a blank
    // end applies through the whole projection. Only a $0 amount removes the row.
    if (!Number.isFinite(startAge)) startAge = 0;
    if (!Number.isFinite(endAge))   endAge   = 200;
    return {
      treatment: r.treatment === 'preferential' ? 'preferential'
               : r.treatment === 'earned' ? 'earned' : 'ordinary',
      amount, startAge, endAge,
    };
  }).filter(r => r.amount > 0);
}

// ─── STEP 1: Federal Tax ──────────────────────────────────────────────────────

export const BASE_YEAR = 2026;
const STD_INFLATION = 0.0275;

// Real IRS 2026 standard deduction (Rev. Proc. 2025-32), anchored at 2026 and
// indexed forward by STD_INFLATION. (Previously a 2023 figure inflated to 2026,
// which resolved to the 2025 amounts — $31,500/$15,750 — one year stale.)
const STD_BASE_YEAR = 2026;
const STD_BASE = { mfj: 32200, single: 16100 };

// ─── Senior deduction constants (age-65+) ─────────────────────────────────────
//
// (A) PERMANENT IRC §63(f) additional standard deduction for taxpayers who are
//     65+. Applies PER qualifying senior (each MFJ spouse who is 65+, or the
//     single filer). 2026 tax-year figures; indexed forward with the same
//     inflation the base standard deduction uses (the base IS indexed here).
const AGE65_ADDL_STD_DED = { mfj: 1650, single: 2000 }; // per senior, 2026 $
const AGE65_ADDL_BASE_YEAR = 2026;

// (B) OBBBA "senior bonus" deduction — TEMPORARY: tax years 2025–2028 ONLY,
//     $0 from 2029. Reduces TAXABLE income only. Several parameters below are
//     the contested part of the statute's reading; keep them as explicit,
//     easy-to-flip constants.
const OBBBA_ACTIVE = { firstYear: 2025, lastYear: 2028 };
const OBBBA_BONUS_PER_SENIOR = 6000;   // max bonus per eligible 65+ person
const OBBBA_BONUS_PER_SPOUSE = true;   // CONTESTED: true → $6k × seniors (max $12k MFJ);
                                       //            false → flat $6k per return, any senior count
const OBBBA_PHASEOUT_START = { mfj: 150000, single: 75000 }; // MAGI where phase-out begins
const OBBBA_PHASEOUT_RATE  = 0.06;     // reduction per $ of MAGI over the threshold
const OBBBA_PHASEOUT_STEP  = 1000;     // round MAGI excess DOWN to this increment before applying rate
// NOTE: OBBBA thresholds/amounts are statutory fixed dollars for 2025–2028 — NOT indexed.

const GA_EXEMPTION_BASE = 2300;
const GA_EXEMPTION_BASE_YEAR = 2026;

// Louisiana: flat 3% individual rate (2025 reform). $12,000/person "annual retirement
// income" exemption for filers age 65+, indexed from 2026 by CPI. La. R.S. 47:44.1.
const LA_RETIREMENT_EXEMPTION_BASE = 12000;
const LA_EXEMPTION_BASE_YEAR = 2026;

// Real IRS 2026 brackets (Rev. Proc. 2025-32), as [taxable-income upper bound, rate].
// (Previously the 2025 thresholds.) calcFederalTax applies these as static 2026
// brackets for all projection years — see the forward-indexing note on calcFederalTax.
const BRACKETS = {
  mfj: [
    [24800, 0.10], [100800, 0.12], [211400, 0.22],
    [403550, 0.24], [512450, 0.32], [768700, 0.35], [Infinity, 0.37],
  ],
  single: [
    [12400, 0.10], [50400, 0.12], [105700, 0.22],
    [201775, 0.24], [256225, 0.32], [640600, 0.35], [Infinity, 0.37],
  ],
};

function inflateAmt(base, fromYear, calendarYear) {
  const years = Math.max(0, calendarYear - fromYear);
  return Math.round(base * Math.pow(1 + STD_INFLATION, years) / 50) * 50;
}

// Federal brackets are anchored at BASE_YEAR (2026) and indexed forward by the
// SAME inflation assumption the standard deduction uses (STD_INFLATION), so the
// 22%/24%/etc. ceilings grow in lockstep with the deduction across the projection.
function bracketInflationFactor(calendarYear) {
  return Math.pow(1 + STD_INFLATION, Math.max(0, calendarYear - BASE_YEAR));
}

function getMarginalRate(taxableIncome, calendarYear, filingStatus) {
  if (taxableIncome <= 0) return 0.10;
  const brackets = BRACKETS[filingStatus] ?? BRACKETS.single;
  const inflFactor = bracketInflationFactor(calendarYear);
  for (const [upper, rate] of brackets) {
    if (upper === Infinity || taxableIncome <= upper * inflFactor) return rate;
  }
  return 0.37;
}

function applyBrackets(taxable, brackets) {
  if (taxable <= 0) return 0;
  let tax = 0;
  let prev = 0;
  for (const [upper, rate] of brackets) {
    if (taxable <= prev) break;
    tax += (Math.min(taxable, upper) - prev) * rate;
    if (upper === Infinity) break;
    prev = upper;
  }
  return tax;
}

export function getStandardDeduction(filingStatus, clientAge, spouseAge, calendarYear = BASE_YEAR) {
  const fs   = STD_BASE[filingStatus] ? filingStatus : 'single';
  const base = inflateAmt(STD_BASE[fs], STD_BASE_YEAR, calendarYear);
  // §63(f) additional standard deduction — one per senior (65+), indexed like the base.
  const perSenior = inflateAmt(AGE65_ADDL_STD_DED[fs], AGE65_ADDL_BASE_YEAR, calendarYear);
  let seniors = (clientAge != null && clientAge >= 65) ? 1 : 0;
  if (fs === 'mfj' && spouseAge != null && spouseAge >= 65) seniors += 1;
  return base + perSenior * seniors;
}

export function calcFederalTax(taxableIncome, filingStatus, calendarYear = BASE_YEAR) {
  if (taxableIncome <= 0) return { tax: 0, effectiveRate: 0 };
  const base = BRACKETS[filingStatus] ?? BRACKETS.single;
  // Index bracket ceilings forward from 2026 by STD_INFLATION (Infinity stays Infinity).
  const f = bracketInflationFactor(calendarYear);
  const brackets = f === 1 ? base : base.map(([upper, rate]) => [upper === Infinity ? Infinity : upper * f, rate]);
  const tax = applyBrackets(taxableIncome, brackets);
  return { tax, effectiveRate: tax / taxableIncome };
}

// ─── Senior Bonus Deduction ───────────────────────────────────────────────────

// OBBBA senior bonus. `magi` is the MAGI used for the phase-out ONLY — it does not
// (and must not) flow into the IRMAA MAGI comparison, which the caller computes
// separately from gross income. Reduces federal TAXABLE income only.
export function calcSeniorBonusDeduction(magi, clientAge, spouseAge, filingStatus, calendarYear) {
  // TEMPORARY: only tax years 2025–2028.
  if (calendarYear < OBBBA_ACTIVE.firstYear || calendarYear > OBBBA_ACTIVE.lastYear) return 0;
  const fs = filingStatus === 'mfj' ? 'mfj' : 'single';
  let seniors = (clientAge != null && clientAge >= 65) ? 1 : 0;
  if (fs === 'mfj' && spouseAge != null && spouseAge >= 65) seniors += 1;
  if (seniors === 0) return 0;

  const maxBonus = OBBBA_BONUS_PER_SPOUSE
    ? OBBBA_BONUS_PER_SENIOR * seniors   // $6k × seniors (max $12k MFJ / $6k single)
    : OBBBA_BONUS_PER_SENIOR;            // flat $6k per return

  // Phase-out: 6% of MAGI over the threshold, with the excess rounded DOWN to the
  // nearest whole $1,000 before the rate is applied.
  const excess        = Math.max(0, magi - OBBBA_PHASEOUT_START[fs]);
  const excessRounded = Math.floor(excess / OBBBA_PHASEOUT_STEP) * OBBBA_PHASEOUT_STEP;
  const reduction     = OBBBA_PHASEOUT_RATE * excessRounded;
  return Math.max(0, maxBonus - reduction);
}

// ─── SS claiming-age benefit adjustment ───────────────────────────────────────
//
// The entered monthly benefit is stated at a BASIS AGE (the age on the client's
// SSA statement). For any CLAIMING AGE we rescale it relative to the PIA (the FRA
// benefit) using SSA's factors, so delaying past FRA produces a genuinely larger
// check and claiming early a smaller one. FRA is derived from birth year (not a
// constant). COLA is applied separately by the engine from a common anchor
// (plan start), so a later claim keeps the COLAs that accrued while waiting.
const SS_DRC_PER_YEAR = 0.08;   // delayed retirement credits, FRA→70 (+8%/yr)
const SS_EARLY_1 = 5 / 900;     // 5/9% per month, first 36 months before FRA
const SS_EARLY_2 = 5 / 1200;    // 5/12% per month beyond 36 months before FRA

// Full retirement age (in years) by birth year — SSA schedule.
export function fraForBirthYear(birthYear) {
  if (birthYear <= 1954) return 66;
  if (birthYear >= 1960) return 67;
  return 66 + (birthYear - 1954) * (2 / 12); // 1955–1959: +2 months per birth year
}

// First RMD age by birth year — SECURE 2.0. Single source of truth: every RMD
// trigger in the engine must derive its start age from here, using the client's
// REAL birth year (from DOB) — never an age-based guess, which lands on the wrong
// side of the 1960 cliff for anyone whose current age straddles a calendar year.
export function rmdAgeForBirthYear(birthYear) {
  if (birthYear >= 1960) return 75;
  if (birthYear >= 1951) return 73;
  return 72;
}

// Benefit multiplier vs the FRA/PIA benefit for a given claiming age.
export function ssBenefitFactor(claimAge, fra) {
  const months = Math.round((claimAge - fra) * 12);
  if (months === 0) return 1;
  if (months > 0) {
    const drcMonths = Math.min(months, Math.round((70 - fra) * 12)); // credits stop at 70
    return 1 + (drcMonths / 12) * SS_DRC_PER_YEAR;
  }
  const early = -months;
  const first = Math.min(early, 36);
  const rest  = Math.max(0, early - 36);
  return Math.max(0, 1 - (first * SS_EARLY_1 + rest * SS_EARLY_2));
}

// Converts an entered benefit stated at `basisAge` into the benefit at `claimAge`
// (both relative to the same PIA). basisAge === claimAge ⇒ 1 (amount unchanged).
export function ssClaimFactor(claimAge, basisAge, birthYear) {
  const fra = fraForBirthYear(birthYear);
  const basisF = ssBenefitFactor(basisAge, fra);
  return basisF > 0 ? ssBenefitFactor(claimAge, fra) / basisF : 1;
}

// ─── STEP 2: SS Taxation ──────────────────────────────────────────────────────

export function calcSSTaxable(provisionalIncome, ssAnnual, filingStatus) {
  if (ssAnnual <= 0) return 0;
  const [t1, t2, cap50] = filingStatus === 'mfj'
    ? [32000, 44000, 6000]
    : [25000, 34000, 4500];
  if (provisionalIncome <= t1) return 0;
  if (provisionalIncome <= t2) return Math.min(0.5 * ssAnnual, 0.5 * (provisionalIncome - t1));
  const tier1 = Math.min(0.5 * ssAnnual, cap50);
  const tier2 = Math.max(0, 0.85 * (provisionalIncome - t2));
  return Math.min(0.85 * ssAnnual, tier1 + tier2);
}

// ─── STEP 3: State Tax ────────────────────────────────────────────────────────

export function calcStateTax(grossTaxable, state, clientAge, spouseAge, filingStatus, stateFlatRate, pensionAnnual = 0, calendarYear = BASE_YEAR, dependents = 0) {
  if (!state || grossTaxable <= 0) return 0;
  const s = state.toUpperCase().trim();
  if (s === 'FL' || s === 'TN') return 0;
  if (s === 'PA') {
    // PA: 3.07% flat on wages/interest/dividends/gains, but retirement income is fully
    // EXEMPT — SS, pensions, IRA/401k distributions, RMDs, AND Roth conversions (for a
    // retiree past 59½) are all untaxed. The engine's state base is dominated by exactly
    // that exempt retirement income (pension + RMD + conversions + gap draws; SS is
    // already excluded by callers), so PA returns 0 like FL/TN. NEVER route PA through
    // OTHER + 3.07% — the generic flat branch would charge phantom state tax on
    // conversions/RMDs that PA does not levy (~$3.1K/yr per $100K converted).
    // KNOWN SIMPLIFICATION: pre-retirement wages and NQ interest/dividend/cap-gain
    // income rows are also returned as 0 here; PA actually taxes those at 3.07%.
    // Callers can't currently pass a non-retirement breakdown — revisit if a working
    // PA client with large taxable investment income shows up.
    return 0;
  }
  if (s === 'GA') {
    const numPersons = (filingStatus === 'mfj' && spouseAge != null && spouseAge >= 65 && clientAge >= 65) ? 2 : 1;
    const exemption = inflateAmt(GA_EXEMPTION_BASE, GA_EXEMPTION_BASE_YEAR, calendarYear) * numPersons;
    return Math.max(0, pensionAnnual - exemption) * 0.0549;
  }
  if (s === 'NC') return grossTaxable * 0.045;
  if (s === 'SC') return grossTaxable * 0.062;
  if (s === 'CA') {
    // CA taxes ALL income except Social Security as ORDINARY — pension (incl. CalSTRS),
    // IRA/401k distributions, conversions, AND capital gains + qualified dividends (NO
    // preferential break). Callers already pass an SS-excluded ordinary base as
    // `grossTaxable`, so SS is out; preferential rows are in it → taxed as ordinary here.
    const isMFJ = filingStatus === 'mfj';
    const caStd = isMFJ ? 11412 : 5706;          // CA 2026 standard deduction
    const caTaxable = Math.max(0, grossTaxable - caStd);
    // CA 2026 progressive brackets as [lowerBound, rate]; MFJ breakpoints = single × 2.
    const brackets = isMFJ
      ? [[0,0.01],[22158,0.02],[52528,0.04],[82904,0.06],[115084,0.08],[145448,0.093],[742958,0.103],[891542,0.113],[1485906,0.123]]
      : [[0,0.01],[11079,0.02],[26264,0.04],[41452,0.06],[57542,0.08],[72724,0.093],[371479,0.103],[445771,0.113],[742953,0.123]];
    let caTax = 0;
    for (let i = 0; i < brackets.length; i++) {
      const lb = brackets[i][0];
      if (caTaxable <= lb) break;
      const ub = i + 1 < brackets.length ? brackets[i + 1][0] : Infinity;
      caTax += (Math.min(caTaxable, ub) - lb) * brackets[i][1];
    }
    // CA exemption credit, filing-status-keyed: personal ($153 single / $306 mfj) + $475
    // per dependent. Uses the 2025 FTB amounts (FTB directs 2026 filers to these).
    // TODO: re-index the $153 / $306 / $475 amounts when FTB publishes 2026 figures.
    const personalCredit = isMFJ ? 306 : 153;
    const exemptionCredit = personalCredit + 475 * (parseFloat(dependents) || 0);
    caTax = Math.max(0, caTax - exemptionCredit);
    // Mental Health Services Tax: a SEPARATE flat 1% surcharge on CA taxable income over
    // $1,000,000 — same threshold for BOTH statuses (NOT doubled for MFJ).
    if (caTaxable > 1000000) caTax += (caTaxable - 1000000) * 0.01;
    return caTax;
  }
  if (s === 'VA') {
    const isMFJ = filingStatus === 'mfj';
    const stdDed = isMFJ ? 16000 : 8000;
    let ageDeduction = 0;
    if (clientAge >= 65) {
      const threshold = isMFJ ? 75000 : 50000;
      ageDeduction = Math.max(0, 12000 - Math.max(0, grossTaxable - threshold));
    }
    const vaTaxable = Math.max(0, grossTaxable - stdDed - ageDeduction);
    let vaTax = 0;
    if (vaTaxable > 17000) vaTax = 3000 * 0.02 + 2000 * 0.03 + 12000 * 0.05 + (vaTaxable - 17000) * 0.0575;
    else if (vaTaxable > 5000) vaTax = 3000 * 0.02 + 2000 * 0.03 + (vaTaxable - 5000) * 0.05;
    else if (vaTaxable > 3000) vaTax = 3000 * 0.02 + (vaTaxable - 3000) * 0.03;
    else vaTax = vaTaxable * 0.02;
    const personalExemption = 930 * (isMFJ ? 2 : 1);
    return Math.max(0, vaTax - personalExemption);
  }
  if (s === 'LA') {
    // Flat 3%. The $12k/person (65+) retirement-income exemption is applied against the
    // ordinary base passed in (pension + IRA/conversion + RMD); capped, so it shaves the
    // exemption off the top regardless of how much retirement income there is.
    const seniors = (clientAge >= 65 ? 1 : 0) +
      (filingStatus === 'mfj' && spouseAge != null && spouseAge >= 65 ? 1 : 0);
    const exemption = inflateAmt(LA_RETIREMENT_EXEMPTION_BASE, LA_EXEMPTION_BASE_YEAR, calendarYear) * seniors;
    return Math.max(0, grossTaxable - exemption) * 0.03;
  }
  const flat = parseFloat(stateFlatRate) || 0;
  return grossTaxable * (flat / 100);
}

// ─── STEP 4: IRMAA ────────────────────────────────────────────────────────────

// 2026 IRMAA — [MAGI upper bound, ANNUAL surcharge per Medicare-enrolled person
// (Part B + Part D combined)]. MAGI above the top tier maps to the top surcharge (never 0).
const IRMAA_MFJ = [
  [218000, 0], [274000, 1148], [342000, 2885], [410000, 4620], [750000, 6356], [Infinity, 6936],
];
const IRMAA_SINGLE = [
  [109000, 0], [137000, 1148], [171000, 2885], [205000, 4620], [500000, 6356], [Infinity, 6936],
];

// IRMAA applies PER Medicare-enrolled person (age >= 65), not per filing status — a spouse
// under 65 is not yet enrolled and adds no surcharge.
export function medicarePersons(clientAge, spouseAge) {
  return (clientAge >= 65 ? 1 : 0) + (spouseAge != null && spouseAge >= 65 ? 1 : 0);
}

// ─── IRMAA indexing (documented assumption #10) ───────────────────────────────
//
// IRMAA brackets are set annually by CMS and historically LAG general inflation,
// so leaving them static overstates future surcharges. We index the tier MAGI
// bounds forward from IRMAA_BASE_YEAR at IRMAA_BRACKET_INFLATION (~1%/yr to model
// that lag). Surcharge AMOUNTS are held flat by default. Both are explicit
// toggles — flip IRMAA_INDEX_BRACKETS=false for fully static 2026 brackets.
const IRMAA_BASE_YEAR           = 2026;
const IRMAA_INDEX_BRACKETS      = true;
const IRMAA_BRACKET_INFLATION   = 0.01;
const IRMAA_INDEX_SURCHARGES    = false;
const IRMAA_SURCHARGE_INFLATION = 0.01;

export function calcIRMAA(magi, filingStatus, persons = 1, calendarYear = BASE_YEAR) {
  if (magi <= 0 || persons <= 0) return 0;
  const tiers = filingStatus === 'mfj' ? IRMAA_MFJ : IRMAA_SINGLE;
  const yrs        = Math.max(0, calendarYear - IRMAA_BASE_YEAR);
  const bracketF   = IRMAA_INDEX_BRACKETS   ? Math.pow(1 + IRMAA_BRACKET_INFLATION,   yrs) : 1;
  const surchargeF = IRMAA_INDEX_SURCHARGES ? Math.pow(1 + IRMAA_SURCHARGE_INFLATION, yrs) : 1;
  for (const [upper, perPerson] of tiers) {
    if (upper === Infinity || magi <= upper * bracketF) return perPerson * surchargeF * persons;
  }
  return 0;
}

// ─── STEP 6: RMD Uniform Lifetime Table ──────────────────────────────────────

export const RMD_TABLE = {
  72:27.4, 73:26.5, 74:25.5, 75:24.6, 76:23.7,
  77:22.9, 78:22.0, 79:21.1, 80:20.2, 81:19.4,
  82:18.5, 83:17.7, 84:16.8, 85:16.0, 86:15.2,
  87:14.4, 88:13.7, 89:12.9, 90:12.2, 91:11.5,
  92:10.8, 93:10.1, 94:9.5,  95:8.9,
};

export function getRMDivisor(age) {
  // Earliest SECURE 2.0 RMD age is 72 (pre-1951 births); the caller already gates
  // on the client's real rmdStartAge, so this only needs to floor at the table min.
  if (age < 72) return null;
  return RMD_TABLE[Math.min(age, 95)] ?? 8.9;
}

// ─── RMD Split Helper ─────────────────────────────────────────────────────────
//
// Two modes:
//   conversion=true  → annuity-first waterfall (protect IRA for conversion)
//   conversion=false → proportional split (Baseline: both buckets drain naturally)
//
// Returns { rmdTotal, annuityRMD, iraRMD }
//
function calcRMDSplit(iraBalance, annuityBalance, age, hasConversion) {
  const divisor = getRMDivisor(age);
  if (!divisor || (iraBalance <= 0 && annuityBalance <= 0)) {
    return { rmdTotal: 0, annuityRMD: 0, iraRMD: 0 };
  }
  const rmdTotal = (iraBalance + annuityBalance) / divisor;
  let annuityRMD, iraRMD;
  if (hasConversion) {
    // Annuity absorbs first — protect convertible IRA
    annuityRMD = Math.min(annuityBalance, rmdTotal);
    iraRMD     = Math.max(0, rmdTotal - annuityRMD);
  } else {
    // Proportional split — realistic no-conversion picture
    const total = iraBalance + annuityBalance;
    annuityRMD = total > 0 ? rmdTotal * (annuityBalance / total) : 0;
    iraRMD     = total > 0 ? rmdTotal * (iraBalance    / total) : 0;
  }
  return { rmdTotal, annuityRMD, iraRMD };
}

// ─── Two-Pass Tax + Gap Solver ────────────────────────────────────────────────
//
// Solves the circular dependency between gap withdrawals and taxes.
//
// Income coverage priority (ALL years, conversion or not):
//   1. SS net of tax
//   2. Pension net of tax
//   3. RMD net of tax
//   4. Roth (tax-free)
//   5. NQ/taxable (after-tax)
//   6. Annuity gap draw (pre-tax → taxable ordinary income)
//   7. IRA gap draw (pre-tax → taxable ordinary income, grossed up)
//
// Conversion cash ALWAYS goes 100% to Roth, never touches expenses.
//
// Pass 1: compute tax on known income (SS+pension+RMD+conv)
//         determine how much gap remains after Roth+NQ
//         estimate annuity/IRA gap draws using pass-1 marginal rate gross-up
// Pass 2: recompute tax on full income stack (adding gap draws)
//         final net income, gap, and all displayed tax figures come from pass 2
//
// Returns: {
//   totalTax, fedTax, stateTax, irmaa,
//   ssTaxable, grossTaxable, taxableIncome, seniorBonus,
//   rothDist, nqGapDraw, annuityGapDraw, iraGapGross,
//   netIncome, incomeGap,
//   taxableReinvestDelta,
//   thisMagi,
// }
//
function solveTaxAndGap({
  // Income sources
  totalSSAnnual, pensionAnnual, rmdTotal, convTaxable,
  // Assets available for gap coverage
  rothBalance, nqBalance, annuityBalance, iraBalance,
  // Tax inputs
  filingStatus, state, stateFlatRate, age, spouseAge,
  calendarYear, stdDed, irmaaLag,
  // Context
  annualExpenses,
  // External conversion tax shortfall (JBF engine only):
  // portion of conversion tax NOT covered by JBF yield — must be sourced
  // from client assets using same waterfall (annuity → NQ → Roth last).
  // Zero for Baseline and standard conversion scenarios.
  externalConvTax = 0,
  // Voluntary IRA draws are disabled inside a conversion window (any year with a
  // conversion) so the IRA is preserved; true for baseline + post-conversion years.
  allowIraDraw = true,
  // Typed income rows: rowOrdinary (ordinary + earned) stacks like pension; rowPreferential
  // (LTCG + QDI) takes the 0/15/20 preferential brackets on top of ordinary taxable income
  // and adds NIIT; rowNII is the net-investment-income base for NIIT. All default 0, so
  // scenarios with no income rows are byte-for-byte unchanged.
  rowOrdinary = 0, rowPreferential = 0, rowNII = 0,
  dependents = 0,   // CA exemption credit only
}) {
  // ── Helper: compute full tax on a given taxable stack ────────────────────────
  const computeTax = (pension, rmd, conv, gapOrdinary) => {
    const ordinaryStack = pension + rmd + conv + gapOrdinary + rowOrdinary;
    // Preferential income raises provisional income (SS taxability) and AGI/MAGI
    // (senior-bonus phase-out + IRMAA), but is taxed at the LTCG/QDI 0/15/20 rates.
    const prov    = ordinaryStack + rowPreferential + 0.5 * totalSSAnnual;
    const ssTax   = calcSSTaxable(prov, totalSSAnnual, filingStatus);
    const agi     = ssTax + ordinaryStack + rowPreferential;
    const bonus   = calcSeniorBonusDeduction(agi, age, spouseAge, filingStatus, calendarYear);
    const gross   = ssTax + ordinaryStack;
    const taxable = Math.max(0, gross - stdDed - bonus);
    const fed     = calcFederalTax(taxable, filingStatus, calendarYear);
    // Stage B: LTCG/QDI stacked above ordinary taxable income (0/15/20), plus NIIT on the
    // net investment income (MAGI ≈ AGI here; IRA/conv are in MAGI but excluded from NII).
    const fedPref = preferentialFedTax(taxable, rowPreferential, filingStatus);
    const niit    = niitTax(rowNII, agi, filingStatus);
    const st      = calcStateTax(pension + rmd + conv + gapOrdinary + rowOrdinary + rowPreferential, state, age, spouseAge, filingStatus, stateFlatRate,
                      pension, calendarYear, dependents);
    const irmaa   = calcIRMAA(irmaaLag, filingStatus, medicarePersons(age, spouseAge), calendarYear);
    return { fedTax: fed.tax + fedPref + niit, stateTax: st, irmaa, totalTax: fed.tax + fedPref + niit + st + irmaa,
             ssTaxable: ssTax, grossTaxable: gross + rowPreferential, taxableIncome: taxable,
             seniorBonus: bonus, effectiveRate: fed.effectiveRate };
  };

  // ── Pass 1: SS+pension+RMD only (drives guaranteed net + gross-up rate) ─────
  const p1NoConv = computeTax(pensionAnnual, rmdTotal, 0, 0);

  // Guaranteed net: SS + pension + RMD minus only the non-conversion tax burden.
  // externalConvTax (JBF shortfall) IS a real cash outflow — included in total need.
  const guaranteedNet1 = Math.max(0,
    totalSSAnnual + pensionAnnual + rmdTotal + rowOrdinary + rowPreferential - p1NoConv.totalTax);

  // ── Two ordered needs: living-expense gap, and outside conversion tax ──────
  // Guaranteed income covers expenses first; any surplus then funds the tax.
  const margRate1   = getMarginalRate(p1NoConv.taxableIncome, calendarYear, filingStatus);
  const grossUpRate = Math.max(0.10, Math.min(0.37, margRate1));
  const denom       = Math.max(0.10, 1 - grossUpRate);

  let guaranteedSurplus = Math.max(0, guaranteedNet1 - annualExpenses);
  let expenseGap        = Math.max(0, annualExpenses - guaranteedNet1);
  let outsideTaxNeed    = Math.max(0, externalConvTax);
  { const d = Math.min(outsideTaxNeed, guaranteedSurplus); outsideTaxNeed -= d; guaranteedSurplus -= d; }

  // Running balances — voluntary draws only (RMDs are forced upstream).
  let rothRemain = rothBalance, nqRemain = nqBalance, annRemain = annuityBalance, iraRemain = iraBalance;
  let rothDist = 0, nqGapDraw = 0, annuityGapGross = 0, iraGapGross = 0;

  // Expense gap: guaranteed → Roth → NQ → annuity → IRA (IRA last-resort, only when allowed)
  { const d = Math.min(expenseGap, rothRemain); rothDist  += d; rothRemain -= d; expenseGap -= d; }
  { const d = Math.min(expenseGap, nqRemain);   nqGapDraw += d; nqRemain   -= d; expenseGap -= d; }
  if (expenseGap > 0 && annRemain > 0) {
    const g = Math.min(annRemain, expenseGap / denom); annuityGapGross += g; annRemain -= g; expenseGap = Math.max(0, expenseGap - g * denom);
  }
  if (allowIraDraw && expenseGap > 0 && iraRemain > 0) {
    const g = Math.min(iraRemain, expenseGap / denom); iraGapGross += g; iraRemain -= g; expenseGap = Math.max(0, expenseGap - g * denom);
  }

  // Outside conversion tax: NQ → annuity → IRA; NEVER Roth (tax must not raid tax-free money)
  { const d = Math.min(outsideTaxNeed, nqRemain); nqGapDraw += d; nqRemain -= d; outsideTaxNeed -= d; }
  if (outsideTaxNeed > 0 && annRemain > 0) {
    const g = Math.min(annRemain, outsideTaxNeed / denom); annuityGapGross += g; annRemain -= g; outsideTaxNeed = Math.max(0, outsideTaxNeed - g * denom);
  }
  if (allowIraDraw && outsideTaxNeed > 0 && iraRemain > 0) {
    const g = Math.min(iraRemain, outsideTaxNeed / denom); iraGapGross += g; iraRemain -= g; outsideTaxNeed = Math.max(0, outsideTaxNeed - g * denom);
  }

  // Residual need after all PERMITTED sources ⇒ funding source exhausted.
  const unmetExpense     = Math.max(0, expenseGap);
  const fundingExhausted = (unmetExpense + Math.max(0, outsideTaxNeed)) > 0.5;

  // ── Pass 2: tax on the full stack (conversion + taxable gap draws) ─────────
  const gapOrdinary = annuityGapGross + iraGapGross;
  const p2 = computeTax(pensionAnnual, rmdTotal, convTaxable, gapOrdinary);

  // Net income = expenses actually funded; gap = the unmet remainder (graceful degrade).
  const netIncome = Math.max(0, annualExpenses - unmetExpense);
  const incomeGap = unmetExpense;

  // RMD surplus → taxableReinvest (SS+pension+RMD net only; unchanged)
  const p2NoConvNoGap = computeTax(pensionAnnual, rmdTotal, 0, 0);
  const ssPensionNet   = Math.max(0, totalSSAnnual + pensionAnnual + rowOrdinary + rowPreferential - p2NoConvNoGap.totalTax);
  const rmdNetAfterTax = Math.max(0, Math.max(0, totalSSAnnual + pensionAnnual + rmdTotal + rowOrdinary + rowPreferential - p2NoConvNoGap.totalTax) - ssPensionNet);
  const expCoveredBySsPension = Math.min(annualExpenses, ssPensionNet);
  const expRemaining   = Math.max(0, annualExpenses - expCoveredBySsPension);
  const rmdUsedForExp  = Math.min(rmdNetAfterTax, expRemaining);
  const taxableReinvestDelta = Math.max(0, rmdNetAfterTax - rmdUsedForExp);

  // MAGI (IRMAA) and displayed grossTaxable BOTH include the taxable gap draws now.
  const thisMagi            = totalSSAnnual + pensionAnnual + rmdTotal + convTaxable + gapOrdinary + rowOrdinary + rowPreferential;
  const displayGrossTaxable = p2.grossTaxable;

  return {
    totalTax:     p2.totalTax,
    fedTax:       p2.fedTax,
    stateTax:     p2.stateTax,
    irmaa:        p2.irmaa,
    ssTaxable:    p2.ssTaxable,   // display now reconciles with the taxed stack (gap included)
    grossTaxable: displayGrossTaxable,
    taxableIncome: p2.taxableIncome,
    seniorBonus:  p2.seniorBonus,
    // Gap coverage
    rothDist,
    nqGapDraw,
    annuityGapDraw: annuityGapGross,
    iraGapGross,
    // Income summary
    netIncome,
    incomeGap,
    fundingExhausted,
    taxableReinvestDelta,
    thisMagi,
  };
}

// ─── Vehicle helpers ──────────────────────────────────────────────────────────

export function vehicleRates(vehicle, scenarioRor) {
  const ror = parseFloat(scenarioRor) / 100 || 0.07;
  switch (vehicle) {
    case 'jbf':      return { grossYield:0.14, distributionRate:0.11, netRetention:0.03 };
    case 'proposed': return { grossYield:ror,  distributionRate:0,    netRetention:ror  };
    default:         return { grossYield:0.07, distributionRate:0,    netRetention:0.07 };
  }
}

// ─── Inherited IRA after-tax value (single-beneficiary 10-year SECURE drawdown) ──
// Replaces the old flat 25% haircut. Models a level 10-year amortized withdrawal of
// the death-year IRA stacked on the heir's income, taxed with DEATH-YEAR-indexed
// federal + state brackets and standard deduction (reusing the engine's existing
// per-calendarYear indexing). Heir income is indexed to the death year with the SAME
// factor the brackets use (bracketInflationFactor), so income and brackets stay locked
// (Interp A). Single beneficiary, MFJ. Memoized by rounded inputs.
// NOTE: annuity is NOT handled here — no annuity basis is tracked in the model, so
// annuity legacy stays on the existing ×0.75 factor (flagged as a follow-up).
const _inheritedIraCache = new Map();
export function inheritedIraEffRate(deathBalance, deathYear, opts = {}) {
  if (!(deathBalance > 0)) return 0;
  const {
    beneficiaryIncome = 100000,   // heir household income (today's dollars), MFJ
    heirState = '',               // defaults to the client's state at the call site
    heirStateFlatRate = 0,
    filingStatus = 'mfj',
    growth = 0.07,                // scenario ROR (post-ret at retirement lines, pre-ret otherwise)
  } = opts;
  const r = growth > 0 ? growth : 0.07;
  const key = `${Math.round(deathBalance / 1000)}|${Math.round(beneficiaryIncome / 1000)}|${Math.round(deathYear)}|${heirState}|${heirStateFlatRate}|${filingStatus}|${r}`;
  const cached = _inheritedIraCache.get(key);
  if (cached !== undefined) return cached;

  // Level amortized 10-year withdrawal of the death-year balance.
  const W = deathBalance * r / (1 - Math.pow(1 + r, -10));
  // Interp A: index the heir income to the death year with the bracket factor.
  const heirInc = beneficiaryIncome * bracketInflationFactor(deathYear);
  const stdDed  = getStandardDeduction(filingStatus, 50, 50, deathYear); // working-age heir → no senior bump
  // Brackets are fixed at deathYear, so all 10 drawdown years are identical: the
  // Σ₁₀[incremental tax] / (W·10) collapses to a single year's incremental tax / W.
  const fedBase = calcFederalTax(Math.max(0, heirInc - stdDed), filingStatus, deathYear).tax;
  const fedWith = calcFederalTax(Math.max(0, heirInc + W - stdDed), filingStatus, deathYear).tax;
  const stBase  = calcStateTax(heirInc,     heirState, 50, 50, filingStatus, heirStateFlatRate, 0, deathYear);
  const stWith  = calcStateTax(heirInc + W, heirState, 50, 50, filingStatus, heirStateFlatRate, 0, deathYear);
  const effRate = Math.max(0, Math.min(0.6, ((fedWith - fedBase) + (stWith - stBase)) / W));
  _inheritedIraCache.set(key, effRate);
  return effRate;
}
export function afterTaxInheritedIra(deathBalance, deathYear, opts = {}) {
  return deathBalance * (1 - inheritedIraEffRate(deathBalance, deathYear, opts));
}

// ─── STEP 5: Sequential Annual Table Engine ───────────────────────────────────

export function calcAnnualTable(params) {
  const {
    clientAge,
    spouseAge = null,
    retirementAge,
    lifeExpectancy = 90,
    filingStatus = 'mfj',
    state = '',
    stateFlatRate = 0,
    dependents = 0,
    iraBalance: initIRA = 0,
    rothBalance: initRoth = 0,
    nonQualBalance: initNQ = 0,
    monthlyContribution = 0,
    preRetirementROR = 0.07,
    postRetirementROR = 0.07,
    // QCD (per-scenario) — see calcJBFTwoBucketAnnualTable for semantics. Off by default.
    qcdAnnual = 0,
    qcdStartAge = null,
    qcdOffsetRmd = false,
    rothNetRetention = 0.03,
    rothDistributionRate = 0.11,
    rothGrossPreRetirement = 0.14,
    ssMonthly = 0,
    ssClaimingAge = 67,
    ssBasisAge,                 // age the entered ssMonthly is stated at; defaults to claiming age
    spouseSSMonthly = 0,
    spouseSSClaimingAge = 67,
    spouseSSBasisAge,
    pensionMonthly = 0,
    ssCOLARate = 0.018,
    monthlyExpenses = 0,
    inflationRate = INFLATION,
    conversions = null,
    survivorEnabled = false,
    survivorAge = 80,
    preRetirementIncome = 0,
    incomeRows = [],
    monthlyRothContribution = 0,
    monthlyOtherContribution = 0,
    annuityBalance: initAnnuity = 0,
    annuityROR: annuityRate = 0.04,
    heirBeneficiaryIncome = 100000,
    heirState,                  // defaults to the client's state below
    heirStateFlatRate,
    birthYear,                  // real DOB year from the form; falls back to an age-guess below
    spouseBirthYear,
    unconvertedIraGrowthRate,   // growth on the un-converted traditional IRA; defaults to the ROR
  } = params;

  // Real birth years thread through from the form's DOB; only guess when absent.
  const clientBirthYear = birthYear ?? (BASE_YEAR - clientAge - 1);
  const spouseBirthYr   = spouseBirthYear ?? (spouseAge != null ? BASE_YEAR - spouseAge - 1 : null);

  // Inherited-IRA haircut options (single beneficiary, MFJ heir).
  const heirOpts = (growth) => ({
    beneficiaryIncome: heirBeneficiaryIncome,
    heirState: heirState ?? state,
    heirStateFlatRate: heirStateFlatRate ?? stateFlatRate,
    growth,
  });

  let ira    = Math.max(0, initIRA);
  let roth   = Math.max(0, initRoth);
  let nq     = Math.max(0, initNQ);
  let annBkt = Math.max(0, initAnnuity);

  const magiHistory = [];
  const rows = [];
  let taxableReinvest = 0;

  const _isSnScenario       = conversions?.vehicle === 'structured_note';
  const snTotalYears        = _isSnScenario ? (conversions.endYear - conversions.startYear + 1) : 0;
  let snExistingRothAtStart = null;
  let snConvAccumulator     = 0;

  const yearsToRetirement = Math.max(0, retirementAge - clientAge);
  const rmdStartAge = rmdAgeForBirthYear(clientBirthYear);
  const hasConversion = conversions !== null;

  // Optional per-year conversion schedule (age → amount). When an explicit schedule
  // is supplied it is the SOURCE OF TRUTH for both which years convert and how much —
  // superseding the flat annualAmount + startYear/endYear window. When absent, every
  // lookup falls back to the legacy window behavior, so scenarios without a custom
  // schedule stay byte-identical. Mirrors the JBF Convert Engine's schedule handling.
  const _convByAge = (conversions && Array.isArray(conversions.schedule) && conversions.schedule.length)
    ? new Map(conversions.schedule.map(e => [Number(e.age), parseFloat(e.amount) || 0]))
    : null;
  // amount for a given projection year: schedule wins when present, else the flat
  // annualAmount inside the start/end window (0 outside it). convYear is 1-based.
  const convAmountForYear = (age, convYear) => {
    if (!conversions) return 0;
    if (_convByAge) return _convByAge.get(age) || 0;
    return (convYear >= conversions.startYear && convYear <= conversions.endYear)
      ? (conversions.annualAmount || 0) : 0;
  };

  // ── Annuity Bonus path (separate early-return engine) ────────────────────────
  if (conversions?.taxMethod === 'annuity_bonus') {
    const annuityGrowthRate = conversions.annuityGrowthRate ?? 0.04;
    const annualDist        = conversions.annualAmount || 0;
    const bonusPct          = conversions.annuityBonusPct ?? 0.18;
    let annuity             = ira * (1 + bonusPct);
    let annBonusPreRetTax   = 0;
    let txReinvest          = 0;

    for (let age = clientAge; age <= lifeExpectancy; age++) {
      const yearFromNow      = age - clientAge;
      const isFirstYear      = age === clientAge;
      const isRetired        = age >= retirementAge;
      const yearFromRet      = Math.max(0, age - retirementAge);
      const calendarYear     = isRetired ? BASE_YEAR + yearFromRet : BASE_YEAR - (retirementAge - age);
      const spouseCurrentAge = spouseAge != null ? spouseAge + yearFromNow : null;
      const effectiveFS      = survivorEnabled && age >= survivorAge ? 'single' : filingStatus;
      const stdDed           = getStandardDeduction(effectiveFS, age, spouseCurrentAge, calendarYear);

      let clientSSAnnual = 0;
      if (isRetired && age >= ssClaimingAge)
        clientSSAnnual = ssMonthly * 12 * ssClaimFactor(ssClaimingAge, ssBasisAge ?? ssClaimingAge, clientBirthYear) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
      let spouseSSAnnual = 0;
      if (isRetired && (!survivorEnabled || age < survivorAge) &&
          spouseSSMonthly > 0 && spouseCurrentAge != null && spouseCurrentAge >= spouseSSClaimingAge)
        spouseSSAnnual = spouseSSMonthly * 12 * ssClaimFactor(spouseSSClaimingAge, spouseSSBasisAge ?? spouseSSClaimingAge, spouseBirthYr) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
      const totalSSAnnual = clientSSAnnual + spouseSSAnnual;
      const pensionAnnual = isRetired ? pensionMonthly * 12 : 0;

      if (isFirstYear) {
        roth = roth * (1 + preRetirementROR) + monthlyRothContribution * 12;
        nq   = Math.max(0, nq * (1 + preRetirementROR)) + monthlyOtherContribution * 12;
        magiHistory.push(0);
        rows.push({
          age, isRetired: false,
          iraBalanceStart: Math.round(annuity), iraBalance: Math.round(annuity),
          rothBalance: Math.round(roth), nonQualBalance: Math.round(nq),
          conversionAmount:0, conversionToRoth:0, rmd:0,
          ssIncome:0, pensionIncome:0, grossIncome:0, rothDistribution:0,
          ssTaxablePct:null, grossTaxableIncome:0, standardDeduction:Math.round(stdDed),
          seniorBonusDeduction:0, taxableIncome:0,
          fedTax:0, stateTax:0, irmaa:0, totalTax:0,
          netIncome:0, annualExpenses:0, incomeGap:0,
          taxableReinvestBalance:0,
          // Dead code (annuity_bonus branch is unreachable): inherited-IRA haircut intentionally NOT applied here.
          legacy: Math.round(annuity * 0.75 + roth + nq),
        });
        continue;
      }

      const iraBalanceStart = Math.round(annuity);
      const startYear       = conversions.startYear ?? 1;
      const endYear         = conversions.endYear ?? 99;
      const convYear        = yearFromNow + 1;
      const isConvActive    = annuity > 0 && convYear >= startYear && convYear <= endYear && isRetired;
      const isPostConvPeriod = isRetired && convYear > endYear;

      let rmdRequired = 0;
      if (isRetired && age >= rmdStartAge && annuity > 0) {
        const div = getRMDivisor(age);
        if (div) rmdRequired = annuity / div;
      }
      const _annGrown0   = annuity * (1 + annuityGrowthRate);
      const _distPreview = isConvActive ? Math.min(annualDist, _annGrown0) : 0;
      const rmd          = (isConvActive && _distPreview >= rmdRequired) ? 0 : rmdRequired;
      const annuityGrown = Math.max(0, annuity - rmd) * (1 + annuityGrowthRate);
      const dist         = isConvActive ? Math.min(annualDist, annuityGrown) : 0;

      let convFedTax=0, convStateTax=0, convTax=0, rmdFedTax=0, rmdStateTax=0, rmdTax=0;
      if (!isRetired && dist > 0) {
        const baseTax = Math.max(0, preRetirementIncome - stdDed);
        const withTax = Math.max(0, preRetirementIncome + dist - stdDed);
        convFedTax   = Math.max(0, calcFederalTax(withTax, effectiveFS, calendarYear).tax - calcFederalTax(baseTax, effectiveFS, calendarYear).tax);
        convStateTax = calcStateTax(dist, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, dist, calendarYear);
        convTax      = convFedTax + convStateTax;
        annBonusPreRetTax += convTax;
      } else if (isRetired && (dist > 0 || rmd > 0)) {
        const provBase   = pensionAnnual + 0.5 * totalSSAnnual;
        const ssTaxBase  = calcSSTaxable(provBase, totalSSAnnual, effectiveFS);
        const bonusBase  = calcSeniorBonusDeduction(ssTaxBase + pensionAnnual, age, spouseCurrentAge, effectiveFS, calendarYear);
        const taxBase    = Math.max(0, ssTaxBase + pensionAnnual - stdDed - bonusBase);
        const fedBase    = calcFederalTax(taxBase, effectiveFS, calendarYear).tax;
        const total      = dist + rmd;
        const provWith   = pensionAnnual + total + 0.5 * totalSSAnnual;
        const ssTaxWith  = calcSSTaxable(provWith, totalSSAnnual, effectiveFS);
        const bonusWith  = calcSeniorBonusDeduction(ssTaxWith + pensionAnnual + total, age, spouseCurrentAge, effectiveFS, calendarYear);
        const taxWith    = Math.max(0, ssTaxWith + pensionAnnual + total - stdDed - bonusWith);
        const totalFed   = Math.max(0, calcFederalTax(taxWith, effectiveFS, calendarYear).tax - fedBase);
        const stWith     = calcStateTax(Math.max(0.01, pensionAnnual + total), state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual + total, calendarYear);
        const stBase2    = pensionAnnual > 0 ? calcStateTax(pensionAnnual, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual, calendarYear) : 0;
        const totalSt    = Math.max(0, stWith - stBase2);
        const totalInc   = totalFed + totalSt;
        const dRatio     = total > 0 ? dist / total : 0;
        rmdFedTax  = Math.round(totalFed * (1 - dRatio));
        rmdStateTax= Math.round(totalSt  * (1 - dRatio));
        rmdTax     = Math.round(totalInc * (1 - dRatio));
        convTax    = totalInc - rmdTax;
        convFedTax = totalFed - rmdFedTax;
        convStateTax = totalSt - rmdStateTax;
      }

      const netToRoth  = Math.max(0, dist - convTax);
      const annuityEnd = Math.max(0, annuityGrown - dist);
      const rothGrown  = Math.max(0, roth * (1 + postRetirementROR) + netToRoth);
      const nqEnd      = !isRetired
        ? Math.max(0, nq * (1 + preRetirementROR)) + monthlyOtherContribution * 12
        : Math.max(0, nq * (1 + postRetirementROR));
      const irmaaLag   = yearFromNow >= 2 ? (magiHistory[yearFromNow - 2] ?? 0) : 0;
      const irmaa      = isRetired ? calcIRMAA(irmaaLag, effectiveFS, medicarePersons(age, spouseCurrentAge), calendarYear) : 0;
      const totalTax   = convTax + rmdTax + irmaa;
      const annualExpenses = isRetired ? monthlyExpenses * 12 * Math.pow(1 + inflationRate, yearFromRet) : 0;

      let rothDist=0, rothWithdrawn=0, incomeGap=0, netIncome=0, taxableReinvestDelta=0;
      if (isRetired) {
        const guaranteedNet = Math.max(0, totalSSAnnual + pensionAnnual + rmd - totalTax);
        const rawGap = Math.max(0, annualExpenses - guaranteedNet);
        rothWithdrawn = Math.min(rawGap, rothGrown);
        rothDist = rothWithdrawn;
        netIncome = guaranteedNet + rothDist;
        incomeGap = Math.max(0, rawGap - rothWithdrawn);
        const ssPensionNet = Math.max(0, totalSSAnnual + pensionAnnual - totalTax);
        const rmdNet = Math.max(0, guaranteedNet - ssPensionNet);
        const expBySsPension = Math.min(annualExpenses, ssPensionNet);
        const expRem = Math.max(0, annualExpenses - expBySsPension);
        taxableReinvestDelta = Math.max(0, rmdNet - Math.min(rmdNet, expRem));
      }
      const rothEnd = Math.max(0, rothGrown - rothWithdrawn);
      if (isRetired) {
        const trGain = txReinvest * postRetirementROR;
        txReinvest = Math.max(0, txReinvest * (1 + postRetirementROR) - trGain * 0.15 + taxableReinvestDelta);
      }

      const provDisp   = isRetired ? (pensionAnnual + dist + rmd + 0.5 * totalSSAnnual) : 0;
      const ssTaxDisp  = isRetired ? calcSSTaxable(provDisp, totalSSAnnual, effectiveFS) : 0;
      const bonusDisp  = isRetired ? calcSeniorBonusDeduction(ssTaxDisp + pensionAnnual + dist + rmd, age, spouseCurrentAge, effectiveFS, calendarYear) : 0;
      const grossDisp  = isRetired ? ssTaxDisp + pensionAnnual + dist + rmd : 0;
      const taxDisp    = isRetired ? Math.max(0, grossDisp - stdDed - bonusDisp) : 0;
      magiHistory.push(isRetired ? totalSSAnnual + pensionAnnual + dist + rmd : 0);

      rows.push({
        age, isRetired, filingStatus: effectiveFS,
        iraBalanceStart, iraBalance: Math.round(annuityEnd),
        rothBalance: Math.round(rothEnd), nonQualBalance: Math.round(nqEnd),
        conversionAmount: Math.round(dist), conversionToRoth: Math.round(netToRoth),
        rmd: Math.round(rmd), ssIncome: Math.round(totalSSAnnual),
        pensionIncome: Math.round(pensionAnnual),
        grossIncome: Math.round(totalSSAnnual + pensionAnnual + dist + rmd),
        rothDistribution: Math.round(rothDist),
        ssTaxablePct: totalSSAnnual > 0 ? Math.round((ssTaxDisp / totalSSAnnual) * 100) : null,
        grossTaxableIncome: Math.round(grossDisp), standardDeduction: Math.round(stdDed),
        seniorBonusDeduction: Math.round(bonusDisp), taxableIncome: Math.round(taxDisp),
        fedTax: Math.round(convFedTax + rmdFedTax), stateTax: Math.round(convStateTax + rmdStateTax),
        irmaa: Math.round(irmaa), totalTax: Math.round(totalTax),
        netIncome: Math.round(netIncome), annualExpenses: Math.round(annualExpenses),
        incomeGap: Math.round(incomeGap), taxableReinvestBalance: Math.round(txReinvest),
        annuityBalance: Math.round(annuityEnd),
        // Dead code (annuity_bonus branch is unreachable): inherited-IRA haircut intentionally NOT applied here.
        legacy: Math.round(annuityEnd * 0.75 + rothEnd + nqEnd + txReinvest),
      });

      annuity = annuityEnd;
      roth    = rothEnd;
      nq      = nqEnd;
    }
    rows.preRetirementTotalTax = Math.round(annBonusPreRetTax);
    return rows;
  }
  // ── END ANNUITY BONUS ────────────────────────────────────────────────────────

  // ── Pre-retirement rows (no conversions) ─────────────────────────────────────
  if (!conversions && clientAge < retirementAge) {
    for (let age = clientAge; age < retirementAge; age++) {
      const iraOpen = ira;
      // Forced RMD applies every year age >= rmdStartAge, even while still working —
      // earned income never defers an IRA RMD. Divisor uses the opening IRA balance.
      const preRMD = (age >= rmdStartAge && iraOpen > 0) ? iraOpen / getRMDivisor(age) : 0;
      ira    = Math.max(0, ira * (1 + preRetirementROR) - preRMD);
      roth   = roth * (1 + preRetirementROR) + monthlyRothContribution * 12;
      nq     = Math.max(0, nq * (1 + preRetirementROR)) + monthlyOtherContribution * 12;
      annBkt = Math.max(0, annBkt * (1 + annuityRate));
      // Earned income (W2/SE) is taxable ordinary income during working years, held flat.
      // It drives federal AND state tax in the Baseline so the no-conversion years are not
      // understated to $0. Zero from retirementAge on (loop ends at retirementAge).
      const earnedIncome    = preRetirementIncome;
      // Pension is paid at all ages in the scenario engine (flat, no COLA); the Baseline
      // must carry the SAME pension in the pre-retirement years so the no-conversion years
      // match the scenario year-for-year (they should differ ONLY by the conversion).
      const prePensionAnnual = pensionMonthly * 12;
      const preSpouseAge     = spouseAge != null ? spouseAge + (age - clientAge) : null;
      // Per-person SS begins the year each person's OWN age reaches their claiming age —
      // independent of the client's retirementAge. Same shared helpers (ssClaimFactor +
      // real birth years) the retired loop and the scenario engine use, so Baseline and
      // scenario SS match year-for-year for the same claiming ages.
      let clientSSAnnual = 0;
      if (age >= ssClaimingAge)
        clientSSAnnual = ssMonthly * 12 * ssClaimFactor(ssClaimingAge, ssBasisAge ?? ssClaimingAge, clientBirthYear) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
      let spouseSSAnnual = 0;
      if ((!survivorEnabled || age < survivorAge) && spouseSSMonthly > 0
          && preSpouseAge != null && preSpouseAge >= spouseSSClaimingAge)
        spouseSSAnnual = spouseSSMonthly * 12 * ssClaimFactor(spouseSSClaimingAge, spouseSSBasisAge ?? spouseSSClaimingAge, spouseBirthYr) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
      const preSSAnnual      = clientSSAnnual + spouseSSAnnual;
      const preCalendarYear  = BASE_YEAR - (retirementAge - age);
      const preStdDed        = getStandardDeduction(filingStatus, age, preSpouseAge, preCalendarYear);
      // SS is taxed on provisional income, the same as every retired row. Earned income,
      // pension, RMD, and typed ORDINARY income rows all stack into ordinary income and
      // provisional income; PREFERENTIAL rows raise provisional + MAGI but tax at 15%.
      const { rowOrdinary, rowPreferential, rowNII } = sumIncomeRows(incomeRows, age);
      const preOrdinary      = earnedIncome + prePensionAnnual + preRMD + rowOrdinary;
      const preSSTaxable     = calcSSTaxable(preOrdinary + rowPreferential + 0.5 * preSSAnnual, preSSAnnual, filingStatus);
      const preBonus         = calcSeniorBonusDeduction(preSSTaxable + preOrdinary + rowPreferential, age, preSpouseAge, filingStatus, preCalendarYear);
      const preOrdGross      = preSSTaxable + preOrdinary;
      const preTaxable       = Math.max(0, preOrdGross - preStdDed - preBonus);
      const preFedTax        = (preOrdGross > 0 ? calcFederalTax(preTaxable, filingStatus, preCalendarYear).tax : 0)
                             + preferentialFedTax(preTaxable, rowPreferential, filingStatus)
                             + niitTax(rowNII, preOrdGross + rowPreferential, filingStatus);
      const preGrossTaxable  = preOrdGross + rowPreferential;
      const preStateTax      = (preOrdinary + rowPreferential) > 0
        ? calcStateTax(preOrdinary + rowPreferential, state, age, preSpouseAge, filingStatus, stateFlatRate, prePensionAnnual, preCalendarYear, dependents) : 0;
      // Conserve the forced distribution: net-of-tax RMD is reinvested into taxable
      // savings (a still-working client doesn't spend it), so legacy stays honest. Income
      // rows appear in both with/without bases so preRMDTax isolates only the RMD's tax.
      if (preRMD > 0) {
        const noRmdOrd   = earnedIncome + prePensionAnnual + rowOrdinary;
        const ssTaxNoRmd = calcSSTaxable(noRmdOrd + rowPreferential + 0.5 * preSSAnnual, preSSAnnual, filingStatus);
        const bonusNoRmd = calcSeniorBonusDeduction(ssTaxNoRmd + noRmdOrd + rowPreferential, age, preSpouseAge, filingStatus, preCalendarYear);
        const noRmdTaxable = Math.max(0, ssTaxNoRmd + noRmdOrd - preStdDed - bonusNoRmd);
        const fedNoRmd   = calcFederalTax(noRmdTaxable, filingStatus, preCalendarYear).tax
                         + preferentialFedTax(noRmdTaxable, rowPreferential, filingStatus)
                         + niitTax(rowNII, ssTaxNoRmd + noRmdOrd + rowPreferential, filingStatus);
        const stNoRmd    = (noRmdOrd + rowPreferential) > 0
          ? calcStateTax(noRmdOrd + rowPreferential, state, age, preSpouseAge, filingStatus, stateFlatRate, prePensionAnnual, preCalendarYear, dependents) : 0;
        const preRMDTax  = Math.max(0, (preFedTax - fedNoRmd) + (preStateTax - stNoRmd));
        nq += Math.max(0, preRMD - preRMDTax);
      }
      rows.push({
        age, isRetired: false,
        iraBalance: Math.round(iraOpen), rothBalance: Math.round(roth),
        nonQualBalance: Math.round(nq), annuityBalance: Math.round(annBkt),
        conversionAmount: 0, rmd: Math.round(preRMD),
        ssIncome: Math.round(preSSAnnual),
        grossIncome: Math.round(earnedIncome + prePensionAnnual + preRMD + rowOrdinary + rowPreferential + preSSAnnual),
        grossTaxableIncome: Math.round(preGrossTaxable),
        standardDeduction: Math.round(preStdDed),
        taxableIncome: Math.round(preTaxable),
        fedTax: Math.round(preFedTax), stateTax: Math.round(preStateTax), irmaa: 0,
        totalTax: Math.round(preFedTax + preStateTax),
        legacy: Math.round(afterTaxInheritedIra(ira, preCalendarYear, heirOpts(preRetirementROR)) + roth + nq + annBkt * 0.75),
        iraDeferredTax: Math.round(ira * inheritedIraEffRate(ira, preCalendarYear, heirOpts(preRetirementROR))),
      });
    }
  }

  // ── Pre-retirement conversions ───────────────────────────────────────────────
  let preRetirementTotalTax = 0;
  if (conversions && clientAge < retirementAge) {
    for (let age = clientAge; age < retirementAge; age++) {
      const iraOpen     = ira;
      // Forced RMD is taken FIRST (before any conversion), every year age >= rmdStartAge,
      // even while still working — earned income never defers an IRA RMD.
      const preRMD = (age >= rmdStartAge && iraOpen > 0) ? iraOpen / getRMDivisor(age) : 0;
      ira = Math.max(0, ira - preRMD);
      const preConvYear = (age - clientAge) + 1;
      const _preSchedAmt = convAmountForYear(age, preConvYear);
      const isConvYear  = _convByAge
        ? _preSchedAmt > 0
        : (preConvYear >= conversions.startYear && preConvYear <= conversions.endYear);
      const isSnNote    = conversions.vehicle === 'structured_note';
      const isSnMatPre  = isSnNote && preConvYear === conversions.endYear;
      const isSnConvPre = isSnNote && preConvYear >= conversions.startYear && preConvYear < conversions.endYear;

      let preYearConvGross=0, preYearTax=0, preYearFedTax=0, preYearStateTax=0;
      const preSpouseAge    = spouseAge != null ? spouseAge + (age - clientAge) : null;
      const preCalendarYear = BASE_YEAR - (retirementAge - age);
      const preStdDed       = getStandardDeduction(filingStatus, age, preSpouseAge, preCalendarYear);

      // Typed income rows: ordinary rows stack above earned income; RMD + conversion
      // stack above that. Preferential rows tax at 15% (+ state). Zero for legacy cases.
      const { rowOrdinary, rowPreferential, rowNII } = sumIncomeRows(incomeRows, age);
      const ordEarnedRows = preRetirementIncome + rowOrdinary;
      let rowOrdFedTax = 0, rowOrdStateTax = 0;
      if (rowOrdinary > 0) {
        const fedE  = calcFederalTax(Math.max(0, preRetirementIncome - preStdDed), filingStatus, preCalendarYear).tax;
        const fedER = calcFederalTax(Math.max(0, ordEarnedRows - preStdDed), filingStatus, preCalendarYear).tax;
        rowOrdFedTax   = Math.max(0, fedER - fedE);
        rowOrdStateTax = calcStateTax(rowOrdinary, state, age, preSpouseAge, filingStatus, stateFlatRate, 0, preCalendarYear);
      }
      // Stage B: preferential stacks 0/15/20 above this path's ordinary taxable income
      // (earned + ordinary rows + RMD), plus NIIT on the net investment income.
      const ordTaxableForPref = Math.max(0, ordEarnedRows + preRMD - preStdDed);
      const rowPrefFed   = preferentialFedTax(ordTaxableForPref, rowPreferential, filingStatus)
                         + niitTax(rowNII, ordEarnedRows + preRMD + rowPreferential, filingStatus);
      const rowPrefState = rowPreferential > 0 ? calcStateTax(rowPreferential, state, age, preSpouseAge, filingStatus, stateFlatRate, 0, preCalendarYear) : 0;

      // RMD stacks into ordinary income above earned income + ordinary rows; conversion
      // (below) stacks above that. Net-of-tax RMD is reinvested to taxable savings.
      let rmdFedTax = 0, rmdStateTax = 0;
      const ordinaryRMDBase = ordEarnedRows + preRMD;
      if (preRMD > 0) {
        const fedInc    = calcFederalTax(Math.max(0, ordEarnedRows - preStdDed), filingStatus, preCalendarYear).tax;
        const fedIncRmd = calcFederalTax(Math.max(0, ordinaryRMDBase - preStdDed), filingStatus, preCalendarYear).tax;
        rmdFedTax   = Math.max(0, fedIncRmd - fedInc);
        rmdStateTax = calcStateTax(preRMD, state, age, preSpouseAge, filingStatus, stateFlatRate, 0, preCalendarYear);
        nq += Math.max(0, preRMD - rmdFedTax - rmdStateTax);
      }

      if (isConvYear) {
        preYearConvGross = _convByAge ? _preSchedAmt : (conversions.annualAmount || 0);
        const baseTax = Math.max(0, ordinaryRMDBase - preStdDed);
        const withTax = Math.max(0, ordinaryRMDBase + preYearConvGross - preStdDed);
        preYearFedTax  = Math.max(0, calcFederalTax(withTax, filingStatus, preCalendarYear).tax - calcFederalTax(baseTax, filingStatus, preCalendarYear).tax);
        preYearStateTax = preYearConvGross > 0
          ? calcStateTax(preYearConvGross, state, age, preSpouseAge, filingStatus, stateFlatRate, preYearConvGross, preCalendarYear) : 0;
        preYearTax = preYearFedTax + preYearStateTax;
        preRetirementTotalTax += preYearTax;

        if (_isSnScenario && (isSnConvPre || isSnMatPre) && snExistingRothAtStart === null)
          snExistingRothAtStart = roth;

        const tm = conversions.taxMethod || 'cash';
        ira = Math.max(0, ira - preYearConvGross);
        if (tm === 'internal') {
          ira = Math.max(0, ira - preYearTax);
          const convNet = Math.max(0, preYearConvGross - preYearTax);
          if (isSnMatPre) {
            snConvAccumulator += convNet;
            roth = (snExistingRothAtStart ?? roth) * Math.pow(1 + preRetirementROR, snTotalYears) + snConvAccumulator * 1.55;
          } else if (isSnConvPre) { snConvAccumulator += convNet; roth = roth + convNet; }
          else { roth = roth * (1 + preRetirementROR) + convNet; }
        } else {
          nq = Math.max(0, nq - preYearTax);
          if (isSnMatPre) {
            snConvAccumulator += preYearConvGross;
            roth = (snExistingRothAtStart ?? roth) * Math.pow(1 + preRetirementROR, snTotalYears) + snConvAccumulator * 1.55;
          } else if (isSnConvPre) { snConvAccumulator += preYearConvGross; roth = roth + preYearConvGross; }
          else { roth = roth * (1 + preRetirementROR) + preYearConvGross; }
        }
      } else {
        roth = roth * (1 + preRetirementROR);
      }
      ira    = Math.max(0, ira * (1 + preRetirementROR));
      nq     = Math.max(0, nq  * (1 + preRetirementROR)) + monthlyOtherContribution * 12;
      roth  += monthlyRothContribution * 12;
      annBkt = Math.max(0, annBkt * (1 + annuityRate));

      rows.push({
        age, isRetired: false,
        iraBalance: Math.round(iraOpen), rothBalance: Math.round(roth),
        nonQualBalance: Math.round(nq), annuityBalance: Math.round(annBkt),
        conversionAmount: Math.round(preYearConvGross), rmd: Math.round(preRMD),
        fedTax: Math.round(preYearFedTax + rmdFedTax + rowOrdFedTax + rowPrefFed),
        stateTax: Math.round(preYearStateTax + rmdStateTax + rowOrdStateTax + rowPrefState),
        totalTax: Math.round(preYearTax + rmdFedTax + rmdStateTax + rowOrdFedTax + rowOrdStateTax + rowPrefFed + rowPrefState),
        legacy: Math.round(afterTaxInheritedIra(ira, preCalendarYear, heirOpts(preRetirementROR)) + roth + nq + annBkt * 0.75),
        iraDeferredTax: Math.round(ira * inheritedIraEffRate(ira, preCalendarYear, heirOpts(preRetirementROR))),
      });
    }
  }

  // ── Retirement loop ──────────────────────────────────────────────────────────
  for (let age = Math.max(retirementAge, clientAge); age <= lifeExpectancy; age++) {
    const yearFromRetirement = age - retirementAge;
    const yearFromNow        = age - clientAge;
    const calendarYear       = BASE_YEAR + yearFromNow;
    const convYear           = yearFromNow + 1;
    const spouseCurrentAge   = spouseAge != null
      ? spouseAge + yearsToRetirement + yearFromRetirement : null;
    const effectiveFS        = survivorEnabled && age >= survivorAge ? 'single' : filingStatus;

    if (conversions?.taxMethod === 'annuity_bonus' && convYear === conversions.startYear)
      ira = ira * (1 + (conversions.annuityBonusPct ?? 0.18));

    const iraBalanceStart = Math.round(ira);

    // ── Conversion first ────────────────────────────────────────────────────────
    let convGross=0, convToRoth=0, convTaxable=0;
    const _convYearAmt = convAmountForYear(age, convYear);
    if (conversions && _convYearAmt > 0) {
      convGross    = Math.min(_convYearAmt, Math.max(0, ira));
      convToRoth   = convGross;
      convTaxable  = convGross;
    }

    // ── RMD split (proportional for Baseline, annuity-first for conversion) ────
    let rmd=0, annuityRMD=0, iraRMD=0;
    if (age >= rmdStartAge && (ira > 0 || annBkt > 0)) {
      const postConvIRA = Math.max(0, ira - convGross);
      const split = calcRMDSplit(postConvIRA, annBkt, age, hasConversion);
      rmd       = split.rmdTotal;
      annuityRMD = split.annuityRMD;
      iraRMD    = split.iraRMD;
    }

    // ── QCD (see calcJBFTwoBucketAnnualTable for full semantics) ────────────────
    const rmdGross = rmd;
    let qcd = 0, qcdExtraFromIra = 0;
    const qcdAge = qcdStartAge != null ? qcdStartAge : rmdStartAge;
    if ((qcdOffsetRmd || qcdAnnual > 0) && age >= qcdAge && ira > convGross) {
      const want = qcdOffsetRmd ? iraRMD : qcdAnnual;
      qcd = Math.max(0, Math.min(want, ira - convGross));
      const iraRMDTaxable = Math.max(0, iraRMD - qcd);
      rmd = annuityRMD + iraRMDTaxable;
      qcdExtraFromIra = Math.max(0, qcd - iraRMD);
    }

    // Post-conv/post-RMD/post-QCD IRA basis
    let postConvIRA = Math.max(0, ira - convGross - iraRMD - qcdExtraFromIra);

    // ── SS ──────────────────────────────────────────────────────────────────────
    let clientSSAnnual = 0;
    if (age >= ssClaimingAge)
      clientSSAnnual = ssMonthly * 12 * ssClaimFactor(ssClaimingAge, ssBasisAge ?? ssClaimingAge, clientBirthYear) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
    let spouseSSAnnual = 0;
    if ((!survivorEnabled || age < survivorAge) && spouseSSMonthly > 0
        && spouseCurrentAge != null && spouseCurrentAge >= spouseSSClaimingAge)
      spouseSSAnnual = spouseSSMonthly * 12 * ssClaimFactor(spouseSSClaimingAge, spouseSSBasisAge ?? spouseSSClaimingAge, spouseBirthYr) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
    const totalSSAnnual = clientSSAnnual + spouseSSAnnual;
    const pensionAnnual = pensionMonthly * 12;
    const annualExpenses = monthlyExpenses * 12 * Math.pow(1 + inflationRate, yearFromRetirement);

    // ── Structured Note flags ────────────────────────────────────────────────────
    const isStructuredNote = conversions?.vehicle === 'structured_note';
    const snStartAge = clientAge + (conversions?.startYear ?? 1) - 1;
    const snEndAge   = clientAge + (conversions?.endYear   ?? 5) - 1;
    const isSnConvPeriod = isStructuredNote && age >= snStartAge && age < snEndAge;
    const isSnMaturity   = isStructuredNote && age === snEndAge;
    const isSnPost       = isStructuredNote && age > snEndAge;

    // ── Incremental conversion tax for routing ───────────────────────────────────
    const stdDed = getStandardDeduction(effectiveFS, age, spouseCurrentAge, calendarYear);
    // Typed income rows active this year — stacked so the conversion's marginal tax
    // reflects the higher starting income (Conversion Cost moves accordingly).
    const { rowOrdinary, rowPreferential, rowNII } = sumIncomeRows(incomeRows, age);
    const isConversionPeriod = conversions != null && convYear >= conversions.startYear && convYear <= conversions.endYear;
    let convTaxIncremental = 0;
    if (convGross > 0) {
      const ordBase     = pensionAnnual + rmd + rowOrdinary;
      const provNoConv  = ordBase + rowPreferential + 0.5 * totalSSAnnual;
      const ssTaxNoConv = calcSSTaxable(provNoConv, totalSSAnnual, effectiveFS);
      const bonusNoConv = calcSeniorBonusDeduction(ssTaxNoConv + ordBase + rowPreferential, age, spouseCurrentAge, effectiveFS, calendarYear);
      const baseT       = Math.max(0, ssTaxNoConv + ordBase - stdDed - bonusNoConv);
      const fedBase     = calcFederalTax(baseT, effectiveFS, calendarYear).tax;
      const provWith    = ordBase + convGross + rowPreferential + 0.5 * totalSSAnnual;
      const ssTaxWith   = calcSSTaxable(provWith, totalSSAnnual, effectiveFS);
      const bonusWith   = calcSeniorBonusDeduction(ssTaxWith + ordBase + convGross + rowPreferential, age, spouseCurrentAge, effectiveFS, calendarYear);
      const withT       = Math.max(0, ssTaxWith + ordBase + convGross - stdDed - bonusWith);
      const fedDelta    = Math.max(0, calcFederalTax(withT, effectiveFS, calendarYear).tax - fedBase);
      const stWithConv  = calcStateTax(Math.max(0.01, ordBase + convGross + rowPreferential), state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual + rmd + convGross, calendarYear);
      const stWoConv    = ordBase + rowPreferential > 0
        ? calcStateTax(ordBase + rowPreferential, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual + rmd, calendarYear) : 0;
      convTaxIncremental = Math.max(0, fedDelta + stWithConv - stWoConv);
    }

    if (conversions?.taxMethod === 'annuity_bonus' && convGross > 0) {
      convToRoth = convGross - convTaxIncremental;
    } else if (conversions?.taxMethod === 'internal' && convGross > 0) {
      convToRoth  = convGross - convTaxIncremental;
      postConvIRA = Math.max(0, postConvIRA - convTaxIncremental);
    }

    // ── IRMAA lag ────────────────────────────────────────────────────────────────
    const irmaaLag = yearFromRetirement >= 2 ? (magiHistory[yearFromRetirement - 2] ?? 0) : 0;

    // ── Two-pass tax + gap solve ──────────────────────────────────────────────────
    const solved = solveTaxAndGap({
      totalSSAnnual, pensionAnnual, rmdTotal: rmd, convTaxable,
      rothBalance:    roth,
      nqBalance:      nq,
      annuityBalance: annBkt,
      iraBalance:     postConvIRA,
      filingStatus:   effectiveFS,
      state, stateFlatRate,
      age, spouseAge: spouseCurrentAge,
      calendarYear, stdDed, irmaaLag,
      annualExpenses,
      allowIraDraw: convTaxable <= 0,   // conversion years disable voluntary IRA draws
      rowOrdinary, rowPreferential, rowNII,
      dependents,
    });

    // ── Balance end-of-year ──────────────────────────────────────────────────────
    const trGain    = taxableReinvest * postRetirementROR;
    taxableReinvest = Math.max(0, taxableReinvest * (1 + postRetirementROR) - trGain * 0.15 + solved.taxableReinvestDelta);

    const iraGrowthRate = (conversions?.taxMethod === 'annuity_bonus' && isConversionPeriod)
      ? (conversions.annuityGrowthRate ?? 0.04)
      : (unconvertedIraGrowthRate != null ? unconvertedIraGrowthRate : postRetirementROR);

    // IRA: post-conv, post-iraRMD, post-iraGapDraw, then grow
    const iraEnd = Math.max(0, (postConvIRA - solved.iraGapGross) * (1 + iraGrowthRate));

    // Annuity: post-annuityRMD, post-annuityGapDraw, then grow
    const annBktEnd = Math.max(0, (annBkt - annuityRMD - solved.annuityGapDraw)) * (1 + annuityRate);

    // NQ: post-nqGapDraw, then grow
    const nqEnd = Math.max(0, (nq - solved.nqGapDraw) * (1 + postRetirementROR));

    // Roth: distribution then growth + conversion proceeds
    const rothGrowthRate = conversions?.taxMethod === 'annuity_bonus'
      ? (isConversionPeriod ? (conversions.annuityGrowthRate ?? 0.04) : postRetirementROR)
      : isSnConvPeriod ? 0 : isSnMaturity ? 0 : isSnPost ? postRetirementROR : rothNetRetention;

    if (_isSnScenario && (isSnConvPeriod || isSnMaturity)) {
      if (isSnConvPeriod && snExistingRothAtStart === null) snExistingRothAtStart = roth;
      snConvAccumulator += convToRoth;
    }

    const rothEnd = (conversions?.taxMethod === 'annuity_bonus' && isConversionPeriod)
      ? Math.max(0, (roth - solved.rothDist + convToRoth) * (1 + rothGrowthRate))
      : isSnMaturity
      ? Math.max(0,
          (snExistingRothAtStart ?? roth) * Math.pow(1 + postRetirementROR, snTotalYears)
          + snConvAccumulator * 1.55 - solved.rothDist)
      : Math.max(0, (roth - solved.rothDist) * (1 + rothGrowthRate) + convToRoth);

    const ssTaxablePct = totalSSAnnual > 0
      ? Math.round((solved.ssTaxable / totalSSAnnual) * 100) : 0;

    rows.push({
      age,
      isRetired: true,
      filingStatus: effectiveFS,
      iraBalanceStart,
      iraBalance:          Math.round(iraBalanceStart),
      rothBalance:         Math.round(rothEnd),
      nonQualBalance:      Math.round(nqEnd),
      annuityBalance:      Math.round(annBktEnd),
      conversionAmount:    Math.round(convGross),
      conversionToRoth:    Math.round(convToRoth),
      // Top federal bracket the last conversion dollar lands in; 0 → table renders "—".
      convMarginalRate:    convGross > 0 ? getMarginalRate(solved.taxableIncome, calendarYear, effectiveFS) : 0,
      rmd:                 Math.round(rmdGross),
      qcd:                 Math.round(qcd),
      ssIncome:            Math.round(totalSSAnnual),
      pensionIncome:       Math.round(pensionAnnual),
      // Investment / other income from typed income rows (NQ dividends, etc.):
      // rowOrdinary stacks like pension; rowPreferential is LTCG/QDI.
      otherIncome:         Math.round(rowOrdinary + rowPreferential),
      // Total income by source = SS + pension + RMD + conversion + other income.
      // (Roth distributions and gap draws are asset drawdowns, shown separately.)
      totalIncome:         Math.round(totalSSAnnual + pensionAnnual + rmd + convGross + rowOrdinary + rowPreferential),
      grossIncome:         Math.round(totalSSAnnual + pensionAnnual + rmd + convGross),
      rothDistribution:    Math.round(solved.rothDist),
      ssTaxablePct,
      grossTaxableIncome:  Math.round(solved.grossTaxable),
      standardDeduction:   Math.round(stdDed),
      seniorBonusDeduction:Math.round(solved.seniorBonus),
      taxableIncome:       Math.round(solved.taxableIncome),
      fedTax:              Math.round(solved.fedTax),
      stateTax:            Math.round(solved.stateTax),
      irmaa:               Math.round(solved.irmaa),
      totalTax:            Math.round(solved.totalTax),
      // Marginal fed+state tax attributable to the conversion — lets the table isolate
      // "Total Base Taxes" (all taxes EXCEPT the Roth conversion tax) for this scenario.
      conversionTaxCost:   Math.round(convTaxIncremental),
      netIncome:           Math.round(solved.netIncome),
      annualExpenses:      Math.round(annualExpenses),
      incomeGap:           Math.round(solved.incomeGap),
      fundingExhausted:    !!solved.fundingExhausted,
      iraGapGross:         Math.round(solved.iraGapGross),
      nqGapDraw:           Math.round(solved.nqGapDraw),
      annuityGapDraw:      Math.round(solved.annuityGapDraw),
      taxableReinvestBalance: Math.round(taxableReinvest),
      legacy: Math.round(afterTaxInheritedIra(iraEnd, calendarYear, heirOpts(iraGrowthRate)) + rothEnd + nqEnd + taxableReinvest + annBktEnd * 0.75),
      iraDeferredTax: Math.round(iraEnd * inheritedIraEffRate(iraEnd, calendarYear, heirOpts(iraGrowthRate))),
    });

    ira    = iraEnd;
    roth   = rothEnd;
    nq     = nqEnd;
    annBkt = annBktEnd;
    magiHistory.push(solved.thisMagi);
  }

  rows.preRetirementTotalTax = Math.round(preRetirementTotalTax);
  return rows;
}

// ─── STEP 9: Baseline ────────────────────────────────────────────────────────

export function calcBaselineTable(params) {
  return calcAnnualTable({ ...params, conversions: null });
}

// ─── STEP 10: Break-Even ─────────────────────────────────────────────────────

export function calcBreakEven(scenarioTable, baselineTable) {
  let lastCrossing = null;
  let wasBelow = true;
  for (const sRow of scenarioTable) {
    const bRow = baselineTable.find(r => r.age === sRow.age);
    if (!bRow) continue;
    const isAbove = sRow.legacy > bRow.legacy;
    if (isAbove && wasBelow) lastCrossing = sRow.age;
    wasBelow = !isAbove;
  }
  return lastCrossing;
}

// ─── Summary helpers ──────────────────────────────────────────────────────────

export function scenarioSummary(scenarioTable, baselineTable) {
  if (!scenarioTable || !baselineTable) return null;
  const lastS = scenarioTable[scenarioTable.length - 1];
  const lastB = baselineTable[baselineTable.length - 1];
  // Deferred tax on the final IRA now uses the per-row inherited-IRA effective rate
  // (stored as iraDeferredTax by the engines), not the old flat 25%. Fallback to 0.25
  // only for legacy tables that predate the field.
  const deferredTax = (row) => row?.iraDeferredTax != null
    ? row.iraDeferredTax : Math.round((row?.iraBalance || 0) * 0.25);
  const totalTaxS = scenarioTable.reduce((s, r) => s + r.totalTax, 0)
    + deferredTax(lastS) + (scenarioTable.preRetirementTotalTax || 0);
  const totalTaxB = baselineTable.reduce((s, r) => s + r.totalTax, 0)
    + deferredTax(lastB) + (baselineTable.preRetirementTotalTax || 0);
  const totalRMDS = scenarioTable.reduce((s, r) => s + (r.rmd || 0), 0);
  const totalRMDB = baselineTable.reduce((s, r) => s + (r.rmd || 0), 0);
  const finalLegacy         = lastS?.legacy ?? 0;
  const finalLegacyBaseline = lastB?.legacy ?? 0;
  return {
    finalLegacy, totalTaxScenario: totalTaxS, totalRMDScenario: totalRMDS,
    preRetirementTotalTax: scenarioTable.preRetirementTotalTax || 0,
    finalLegacyBaseline, totalTaxBaseline: totalTaxB, totalRMDBaseline: totalRMDB,
    legacyDelta:   finalLegacy - finalLegacyBaseline,
    totalTaxDelta: totalTaxB - totalTaxS,
    totalRMDDelta: totalRMDB - totalRMDS,
  };
}

// ─── JBF Two-Bucket Engine ────────────────────────────────────────────────────

export const RISK_TIERS = { L: 0.06, M: 0.07, H: 0.09 };

const JBF_DIST_RATE = 0.11;
const JBF_RETENTION = 0.03;

// ─── CE Portfolio Tiers (revised Aug 6 2026) ─────────────────────────────────
// Risk-linked SN/ETF portfolio tiers. Each tier's planningRate is the engine
// DISTRIBUTION rate. Option A mechanics (locked Aug 4 2026, unchanged): the engine
// sleeve is modeled FLAT — gross = distribution = planningRate, retention 0.
// Aug 6 2026 retune (ETF-forward): 2026 evidence (July drawdown stress test — sleeve
// cash continuity held; Gibbons paid 2:1 vs the 11% plan) moved the mixes ETF-heavy
// and rates to 9/10/11. SN terms are a dial (BondNav custom quotes — barriers/underliers
// risked up or down per prospect) — anchor ref: 9.75% SPX/NDX worst-of w/ memory,
// 60% principal barrier. No growth is claimed on the engine sleeve.
// Scenarios WITHOUT a tier run the legacy 14/11/3 mechanic byte-identical.
export const CE_TIERS = {
  T1: { key: 'T1', label: 'Balanced Income', sub: '50% Structured Notes / 50% Income ETFs', snPct: 0.50, etfPct: 0.50, planningRate: 0.09 },
  T2: { key: 'T2', label: 'Income Blend',    sub: '75% Income ETFs / 25% Notes',            snPct: 0.25, etfPct: 0.75, planningRate: 0.10 },
  T3: { key: 'T3', label: 'Enhanced Income', sub: '100% Income ETFs',                       snPct: 0.00, etfPct: 1.00, planningRate: 0.11 },
};

// Risk Score (1–100) → tier. Bands locked Aug 4 2026: ≤40 → T1, 41–70 → T2, 71+ → T3.
// Display bands in riskScore.js use the SAME cutoffs so score → band → portfolio agree.
export function tierForRiskScore(composite) {
  const s = parseFloat(composite);
  if (!Number.isFinite(s)) return null;
  if (s <= 40) return 'T1';
  if (s <= 70) return 'T2';
  return 'T3';
}

// ─── Advisory fee (CE / Bucket A only) ────────────────────────────────────────
// Flat-by-tier: the WHOLE balance is charged the single rate for the tier it lands
// in (not marginal/blended). Deducted in arrears on the end-of-year Bucket A balance.
// Off by default (advisoryFeeEnabled=false) so existing plans stay byte-identical.
export function advisoryFeeRate(balance) {
  if (balance >= 1_000_000) return 0.0085;
  if (balance >=   500_000) return 0.0090;
  if (balance >=   250_000) return 0.0095;
  return 0.0100;
}

// ─── CE Year-1 cost-to-start + LTFI (display-only, shared by App + PrintPlan) ──
// Coverage = the Year-1 CE bucket seed throwing 11% dividends, prorated by the
// months it runs in Year 1. Only Year 1 is prorated; Year 2+ assume 12 months.
export function ceYear1Coverage(year1CESeed, year1CEMonths, distRate = JBF_DIST_RATE) {
  const months = Math.min(12, Math.max(1, parseInt(year1CEMonths, 10) || 12));
  return Math.round((year1CESeed || 0) * distRate * (months / 12));
}
export function ceCostToStart(year1ConversionTax, year1CESeed, year1CEMonths, distRate = JBF_DIST_RATE) {
  return Math.max(0, Math.round((year1ConversionTax || 0) - ceYear1Coverage(year1CESeed, year1CEMonths, distRate)));
}
// Tax-Free Lifetime Income = end-of-conversions Roth × yield (default 6%).
export function ltfiPerYear(endOfConversionsRoth, ltfiYieldPct) {
  const y = (parseFloat(ltfiYieldPct) || 6) / 100;
  return Math.round((endOfConversionsRoth || 0) * y);
}

// ─── Conversion schedule ──────────────────────────────────────────────────────
//
// The flexible per-year conversion schedule is an array of { age, amount } — one
// entry per conversion year. It is the single source of truth the engine reads
// each projection year. `deriveConversionSchedule` is the TWO-TIER PRESET: it
// expands the legacy { year1Amount, conversionAmount, startYear, endYear } model
// into an explicit schedule. Used both to migrate saved scenarios that predate
// the array and as the "Apply preset" generator in the UI, so the two behave
// identically.
export function deriveConversionSchedule(scenario, clientAge) {
  const start  = Math.max(1, parseInt(scenario.startYear, 10) || 1);
  const end    = Math.max(start, parseInt(scenario.endYear, 10) || start);
  const seed   = scenario.engineSeedSource === 'existing_roth'
    ? (parseFloat(scenario.year1ConversionAmount) || parseFloat(scenario.conversionAmount) || 0)
    : (parseFloat(scenario.year1Amount) || parseFloat(scenario.conversionAmount) || 0);
  const annual = parseFloat(scenario.conversionAmount) || 0;
  const sched = [];
  for (let convYear = start; convYear <= end; convYear++) {
    sched.push({
      age: clientAge + (convYear - 1),
      amount: convYear === start ? seed : annual,
    });
  }
  return sched;
}

// Resolve the schedule the engine should use: an explicit array if present,
// otherwise derive it from the legacy two-tier fields (backward compatibility).
export function resolveConversionSchedule(scenario, clientAge) {
  if (Array.isArray(scenario.conversionSchedule) && scenario.conversionSchedule.length) {
    return scenario.conversionSchedule
      .map(e => ({ age: Number(e.age), amount: parseFloat(e.amount) || 0 }))
      .filter(e => Number.isFinite(e.age));
  }
  return deriveConversionSchedule(scenario, clientAge);
}

function taxForIncome({ ssTaxable, pensionAnnual, rmd, conversion, extraOrdinary = 0, extraPreferential = 0, filingStatus, state, age, spouseAge, stateFlatRate, calendarYear = BASE_YEAR }) {
  // extraOrdinary = typed ordinary/earned income rows (stack as ordinary). extraPreferential
  // = qualified div + LTCG rows: they lift the AGI/phase-out proxy and the state base, but
  // NOT the federal ordinary taxable income (their flat 15% is applied by the caller and
  // cancels in the conversion delta). Both default 0 → legacy behavior unchanged.
  const grossTaxable = ssTaxable + pensionAnnual + rmd + conversion + extraOrdinary;
  const agiProxy     = grossTaxable + extraPreferential;
  // Senior deductions are computed PER PROJECTION YEAR from each spouse's age that
  // year (age = client's current-year age, spouseAge = spouse's current-year age)
  // and the calendar year. They reduce the FEDERAL bracket base ONLY. The IRMAA
  // MAGI is computed separately by the caller from GROSS income and must not net
  // these out — that separation is what buys extra top-of-22% conversion room
  // without moving the IRMAA MAGI. `grossTaxable` here is the ordinary-income AGI
  // proxy used for the OBBBA phase-out (SS-taxable + pension + RMD + conversion).
  const stdDed      = getStandardDeduction(filingStatus, age, spouseAge, calendarYear);
  const seniorBonus = calcSeniorBonusDeduction(agiProxy, age, spouseAge, filingStatus, calendarYear);
  const taxableIncome = Math.max(0, grossTaxable - stdDed - seniorBonus);
  const fed = calcFederalTax(taxableIncome, filingStatus, calendarYear);
  const st  = calcStateTax(pensionAnnual + extraOrdinary + extraPreferential, state, age, spouseAge, filingStatus, stateFlatRate, pensionAnnual + rmd, calendarYear);
  return { tax: fed.tax + st, effectiveRate: fed.effectiveRate, grossTaxable, taxableIncome, stdDed, seniorBonus };
}

export function calcJBFTwoBucketAnnualTable(params) {
  const {
    clientAge,
    spouseAge = null,
    retirementAge,
    lifeExpectancy = 90,
    filingStatus = 'mfj',
    state = '',
    stateFlatRate = 0,
    dependents = 0,
    iraBalance:    initIRA = 0,
    existingRoth:  initExistingRoth = 0,
    nonQualBalance: initNQ = 0,
    monthlyContribution = 0,
    preRetirementROR  = 0.07,
    postRetirementROR = 0.07,
    ssMonthly = 0, ssClaimingAge = 67, ssBasisAge,
    spouseSSMonthly = 0, spouseSSClaimingAge = 67, spouseSSBasisAge,
    pensionMonthly = 0, ssCOLARate = 0.018,
    monthlyExpenses = 0, inflationRate = INFLATION,
    survivorEnabled = false, survivorAge = 80,
    year1Amount, year1ConversionAmount, annualAmount,
    startYear, endYear,
    conversionSchedule = null,
    year1TaxMethod = 'cash',
    years2PlusTaxMethod = 'jbf_distribution',
    // QCD (per-scenario): send up to `qcdAnnual` of IRA distributions to charity each year
    // from `qcdStartAge` on — excluded from taxable income, satisfies RMD first, draws the
    // IRA down. `qcdOffsetRmd` auto-sizes the QCD to that year's IRA RMD (residual-IRA mode).
    // Off (0 / null) by default so existing scenarios are byte-identical.
    qcdAnnual = 0,
    qcdStartAge = null,
    qcdOffsetRmd = false,
    ceTargetSize = 0,
    rothDistributionRate = 0.11,
    riskTier = 'M',
    // CE tier rates (Aug 2026). Defaults = legacy 14/11/3 mechanic, byte-identical for
    // scenarios without a tier. Tiered scenarios pass ceDistRate = planningRate and
    // ceRetention = 0 (Option A: flat engine principal — coupons distribute, no growth).
    ceDistRate = JBF_DIST_RATE,
    ceRetention = JBF_RETENTION,
    // TFLI Bucket (Aug 2026): per-scenario THIRD sleeve. Each conversion year can route
    // part of that year's conversion into a TFLI bucket (e.g. an SN income portfolio held
    // inside the Roth) that pays `tfliYield` of its balance as TAX-FREE income into
    // cash-flow coverage. Principal stays flat (the yield is fully distributed) and the
    // bucket pays for LIFE — it never merges into Standard Roth at end of conversions.
    // Off by default (tfliEnabled false / empty schedule) → existing scenarios are
    // byte-identical.
    tfliEnabled = false,
    tfliSchedule = null,   // [{ age, amount }] — $ of that age's conversion routed to TFLI
    tfliYield = 0.08,      // income yield distributed tax-free (UI suggests 6–10%)
    preRetirementIncome = 0,
    incomeRows = [],
    monthlyRothContribution = 0,
    monthlyOtherContribution = 0,
    includeRothContribInJBF = false,
    engineSeedSource = 'nonqual',
    annuityBalance: initAnnuity = 0,
    annuityROR: annuityRate = 0.04,
    heirBeneficiaryIncome = 100000,
    heirState,                  // defaults to the client's state below
    heirStateFlatRate,
    year1CEMonths = 12,         // months the Year-1 CE seed throws dividends (1..12)
    advisoryFeeEnabled = false, // when true, deduct a flat-by-tier advisory fee from Bucket A
    birthYear,                  // real DOB year from the form; falls back to an age-guess below
    spouseBirthYear,
    unconvertedIraGrowthRate,   // growth on the un-converted traditional IRA; defaults to the ROR
  } = params;

  // Real birth years thread through from the form's DOB; only guess when absent.
  const clientBirthYear = birthYear ?? (BASE_YEAR - clientAge - 1);
  const spouseBirthYr   = spouseBirthYear ?? (spouseAge != null ? BASE_YEAR - spouseAge - 1 : null);

  // Inherited-IRA haircut options (single beneficiary, MFJ heir).
  const heirOpts = (growth) => ({
    beneficiaryIncome: heirBeneficiaryIncome,
    heirState: heirState ?? state,
    heirStateFlatRate: heirStateFlatRate ?? stateFlatRate,
    growth,
  });

  const riskROR = RISK_TIERS[riskTier] ?? 0.07;

  let ira          = Math.max(0, initIRA);
  let jbfBucket    = 0;
  let standardRoth = 0;
  let existingRoth = Math.max(0, initExistingRoth);
  let nq           = Math.max(0, initNQ);
  let annBkt       = Math.max(0, initAnnuity);
  let taxableReinvest  = 0;
  let year1TopUpFromNQ = 0;
  let year1CESeed      = 0;   // Year-1 CE bucket after top-up (basis for cost-to-start)
  const ceTarget = parseFloat(ceTargetSize) || 0;
  let ceContributed = 0;
  let ceFullyFundedAge = null;

  // ── TFLI bucket state ────────────────────────────────────────────────────────
  const tfliOn = !!tfliEnabled;
  const tfliByAge = new Map();
  if (tfliOn && Array.isArray(tfliSchedule)) {
    tfliSchedule.forEach(e => {
      const a = Number(e.age);
      const amt = parseFloat(e.amount) || 0;
      if (Number.isFinite(a) && amt > 0) tfliByAge.set(a, amt);
    });
  }
  let tfliBucket = 0;

  if (engineSeedSource === 'existing_roth') {
    // Seed the Convert Engine from existing Roth, but CAP the engine at the CE target.
    // The excess above target does NOT run on the 14%-gross engine — it goes straight to
    // Standard Roth (grows at the risk ROR). With no target set, seed the whole balance
    // (legacy behavior). New-sleeve / nonqual seeding is untouched.
    const seedToEngine = ceTarget > 0
      ? Math.min(Math.max(0, initExistingRoth), ceTarget)
      : Math.max(0, initExistingRoth);
    jbfBucket     = seedToEngine;
    standardRoth += Math.max(0, initExistingRoth - seedToEngine);
    existingRoth  = 0;
    ceContributed = seedToEngine;
  }

  const magiHistory = [];
  const rows = [];
  let totalExternalTaxPaid = 0;
  let totalJBFTaxFunded    = 0;
  let peakConvTaxCost      = 0;   // SAFETY: max annual conversion tax across the whole schedule
  let cumulativeAdvisoryFee = 0;  // running total of the CE advisory fee (Bucket A, when enabled)
  let conversionCompleteRow = null;

  const rmdStartAge = rmdAgeForBirthYear(clientBirthYear);

  // ── Per-year conversion schedule (source of truth) ──────────────────────────
  // Use the explicit schedule if supplied; otherwise derive the two-tier preset
  // from the flat params so callers that still pass year1Amount/annualAmount work
  // unchanged. The schedule DEFINES the conversion window: a year converts iff it
  // has a scheduled amount > 0. firstConvAge/lastConvAge drive Year-1 seeding and
  // the post-conversion bucket merge — matching the old startYear/endYear window
  // exactly when the schedule is a contiguous two-tier expansion.
  const _schedule = (Array.isArray(conversionSchedule) && conversionSchedule.length)
    ? conversionSchedule
    : deriveConversionSchedule(
        { year1Amount, year1ConversionAmount, conversionAmount: annualAmount, startYear, endYear, engineSeedSource },
        clientAge,
      );
  const scheduleByAge = new Map();
  _schedule.forEach(e => {
    const a = Number(e.age);
    const amt = parseFloat(e.amount) || 0;
    if (Number.isFinite(a) && amt > 0) scheduleByAge.set(a, amt);
  });
  const fundedAges  = [...scheduleByAge.keys()].sort((x, y) => x - y);
  const firstConvAge = fundedAges.length ? fundedAges[0] : Infinity;
  const lastConvAge  = fundedAges.length ? fundedAges[fundedAges.length - 1] : -Infinity;

  // P1.1 SN maturity payoff (Regime B): bumps keyed by maturity age, from the resolved schedule
  const _snConvRows = fundedAges.map(a => ({ age: a, conversion: scheduleByAge.get(a) ?? 0 }));
  const _snBumps = snMaturityBumps({
    snPayoff: params.snPayoff,
    snBaseRate: params.snBaseRate,
    snTenor: params.snTenor,
    snMatLag: params.snMatLag,
    snNoteStartOffset: params.snNoteStartOffset,
  }, _snConvRows);

  for (let age = clientAge; age <= lifeExpectancy; age++) {
    const yearFromNow        = age - clientAge;
    const yearFromRetirement = Math.max(0, age - retirementAge);
    const calendarYear       = BASE_YEAR + yearFromNow;
    const convYear           = yearFromNow + 1;
    const isRetired          = age >= retirementAge;
    const spouseCurrentAge   = spouseAge != null ? spouseAge + yearFromNow : null;
    const effectiveFS        = survivorEnabled && age >= survivorAge ? 'single' : filingStatus;

    // Window is driven by the schedule: a year converts iff it has a funded entry.
    const scheduledAmount = scheduleByAge.get(age) ?? 0;
    const isYear1Conv    = age === firstConvAge;
    const isConvYear     = scheduledAmount > 0;
    const isSubseqConv   = isConvYear && !isYear1Conv;
    const postConversion = age > lastConvAge;

    // ── SS / Pension ────────────────────────────────────────────────────────────
    let clientSSAnnual = 0;
    if (age >= ssClaimingAge)
      clientSSAnnual = ssMonthly * 12 * ssClaimFactor(ssClaimingAge, ssBasisAge ?? ssClaimingAge, clientBirthYear) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
    let spouseSSAnnual = 0;
    if ((!survivorEnabled || age < survivorAge) && spouseSSMonthly > 0
        && spouseCurrentAge != null && spouseCurrentAge >= spouseSSClaimingAge)
      spouseSSAnnual = spouseSSMonthly * 12 * ssClaimFactor(spouseSSClaimingAge, spouseSSBasisAge ?? spouseSSClaimingAge, spouseBirthYr) * Math.pow(1 + ssCOLARate, Math.max(0, age - clientAge));
    const totalSSAnnual = clientSSAnnual + spouseSSAnnual;
    const pensionAnnual = pensionMonthly * 12;

    // ── RMD split: annuity-first (JBF always has conversion active) ─────────────
    // RMDs are forced every year age >= rmdStartAge, INDEPENDENT of retirement/earned
    // income. A client still working part-time past their RMD age must still take IRA
    // RMDs (the "still-working" deferral is an active-employer-401(k) rule, never IRAs).
    // The RMD base is the PRIOR Dec-31 balance (`ira` at the top of the loop), NEVER
    // net of this year's conversion: the first dollars out of an IRA in an RMD year
    // count toward the RMD, and an RMD can never be converted.
    let rmd=0, annuityRMD=0, iraRMD=0;
    if (age >= rmdStartAge && (ira > 0 || annBkt > 0)) {
      const split = calcRMDSplit(ira, annBkt, age, true);
      rmd       = split.rmdTotal;
      annuityRMD = split.annuityRMD;
      iraRMD    = split.iraRMD;
    }

    // ── Conversion amount (from schedule; the IRA RMD comes out first) ──────────
    const convAmount = isConvYear ? Math.min(scheduledAmount, Math.max(0, ira - iraRMD)) : 0;

    // ── QCD (Qualified Charitable Distribution) ─────────────────────────────────
    // Gross RMD is the legal amount (shown in the RMD column); the QCD satisfies it first,
    // making that portion non-taxable, and draws the IRA down. Advisor keeps qcdAnnual within
    // the annual QCD limit (~$115k for 2026, indexed); we only cap it at the available IRA.
    const rmdGross = rmd;
    let qcd = 0, qcdExtraFromIra = 0;
    const qcdAge = qcdStartAge != null ? qcdStartAge : rmdStartAge;
    if ((qcdOffsetRmd || qcdAnnual > 0) && age >= qcdAge && ira > convAmount) {
      const want = qcdOffsetRmd ? iraRMD : qcdAnnual;
      qcd = Math.max(0, Math.min(want, ira - convAmount));
      const iraRMDTaxable = Math.max(0, iraRMD - qcd);   // QCD offsets the IRA RMD first
      rmd = annuityRMD + iraRMDTaxable;                   // taxable RMD used downstream
      qcdExtraFromIra = Math.max(0, qcd - iraRMD);        // QCD beyond RMD = extra IRA outflow
    }

    const expYears       = Math.max(0, age - retirementAge);
    const annualExpenses = isRetired ? monthlyExpenses * 12 * Math.pow(1 + inflationRate, expYears) : 0;
    const stdDed         = getStandardDeduction(effectiveFS, age, spouseCurrentAge, calendarYear);

    // Typed income rows active this year (Stage A): stacked so the conversion's marginal
    // tax reflects the higher starting income (Conversion Cost moves accordingly).
    const { rowOrdinary, rowPreferential, rowNII } = sumIncomeRows(incomeRows, age);

    // ── Baseline tax (no conversion) for incremental conv tax calc ──────────────
    const provNoConv  = (pensionAnnual + rmd + rowOrdinary) + rowPreferential + 0.5 * totalSSAnnual;
    const ssTaxNoConv = calcSSTaxable(provNoConv, totalSSAnnual, effectiveFS);
    const bonusNoConv = calcSeniorBonusDeduction(ssTaxNoConv + pensionAnnual + rmd + rowOrdinary + rowPreferential, age, spouseCurrentAge, effectiveFS, calendarYear);
    const baselineTaxRes = taxForIncome({
      ssTaxable: ssTaxNoConv, pensionAnnual, rmd, conversion: 0,
      extraOrdinary: rowOrdinary, extraPreferential: rowPreferential,
      stdDed, seniorBonus: bonusNoConv, filingStatus: effectiveFS,
      state, age, spouseAge: spouseCurrentAge, stateFlatRate, calendarYear,
    });

    // ── Conversion tax helper ───────────────────────────────────────────────────
    const _convTax = (convAmt) => {
      if (!isRetired) {
        const base = preRetirementIncome + pensionAnnual + rowOrdinary;
        const fedDelta = calcFederalTax(Math.max(0, base + convAmt - stdDed), effectiveFS, calendarYear).tax
                       - calcFederalTax(Math.max(0, base - stdDed), effectiveFS, calendarYear).tax;
        const convState = convAmt > 0
          ? calcStateTax(convAmt, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, convAmt, calendarYear) : 0;
        return Math.max(0, fedDelta + convState);
      }
      const provWith  = (pensionAnnual + rmd + rowOrdinary + convAmt) + rowPreferential + 0.5 * totalSSAnnual;
      const ssTaxWith = calcSSTaxable(provWith, totalSSAnnual, effectiveFS);
      const bonusWith = calcSeniorBonusDeduction(ssTaxWith + pensionAnnual + rmd + rowOrdinary + rowPreferential + convAmt, age, spouseCurrentAge, effectiveFS, calendarYear);
      const withRes   = taxForIncome({ ssTaxable: ssTaxWith, pensionAnnual, rmd, conversion: convAmt, extraOrdinary: rowOrdinary, extraPreferential: rowPreferential, stdDed, seniorBonus: bonusWith, filingStatus: effectiveFS, state, age, spouseAge: spouseCurrentAge, stateFlatRate, calendarYear });
      const stateWithConv = calcStateTax(Math.max(0.01, pensionAnnual + rmd + rowOrdinary + rowPreferential + convAmt), state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual + rmd + convAmt, calendarYear);
      const stateWoConv   = (pensionAnnual + rmd + rowOrdinary + rowPreferential) > 0
        ? calcStateTax(pensionAnnual + rmd + rowOrdinary + rowPreferential, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual + rmd, calendarYear) : 0;
      return Math.max(0, withRes.tax - baselineTaxRes.tax + Math.max(0, stateWithConv - stateWoConv));
    };

    // ── Conversion routing ──────────────────────────────────────────────────────
    let jbfYieldUsedForTax=0, jbfSurplusToRoth=0, externalTaxThisYear=0;
    // Share of this year's conversion routed to each bucket, hoisted out of the
    // conversion block so the Year-1 coverage block below can reprice a withheld
    // amount against the seed it actually produces. 0 in non-conversion years.
    let convCEShare = 0, convTfliShare = 0;
    let newJBFSeed=0, newStandardRoth=0, convTaxCost=0, rothTaxPaidThisYear=0;
    let ceNewMoneyYield=0;   // distributions captured on this year's January CE top-up
    let tfliAllocThisYear=0, newTfliSeed=0;

    if (isConvYear) {
      convTaxCost = _convTax(convAmount);
      if (convTaxCost > peakConvTaxCost) peakConvTaxCost = convTaxCost;

      // TFLI carve-out: this year's TFLI allocation comes off the top of the conversion;
      // the remainder runs the existing CE-fill / Standard-Roth spill unchanged. $0 when
      // the feature is off, so the split below is untouched for existing scenarios.
      tfliAllocThisYear = tfliOn ? Math.min(tfliByAge.get(age) ?? 0, convAmount) : 0;
      newTfliSeed = tfliAllocThisYear;
      const convForBuckets = convAmount - tfliAllocThisYear;

      // Fill the engine to ceTarget first; spill the remainder of this year to Standard Roth.
      let toCE, toRoth;
      if (ceTarget > 0) {
        const room = Math.max(0, ceTarget - ceContributed);
        toCE   = Math.min(convForBuckets, room);
        toRoth = convForBuckets - toCE;
      } else {
        toCE   = isYear1Conv ? convForBuckets : 0;   // legacy: Year 1 seeds, rest → Roth
        toRoth = isYear1Conv ? 0 : convForBuckets;
      }
      ceContributed += toCE;
      convCEShare   = convAmount > 0 ? toCE / convAmount : 0;
      convTfliShare = convAmount > 0 ? tfliAllocThisYear / convAmount : 0;
      if (ceTarget > 0 && ceFullyFundedAge == null && ceContributed >= ceTarget - 1) ceFullyFundedAge = age;

      // January top-ups: while the engine is still filling to target, each year's CE
      // top-up (toCE) is converted in early January, so it captures a FULL year of the
      // 11% distribution in its own conversion year. This matches the full-year 3%
      // retention the new seed already earns below — new CE money runs the whole
      // 14/11/3 mechanic from day one, instead of earning 3% gross its first year.
      // Year 1 is untouched: the unified Year-1 block below reprices coverage from
      // year1CESeed × year1CEMonths. Once the engine is at target, toCE is $0 and
      // this term vanishes, so fully-funded years are unchanged.
      ceNewMoneyYield = isSubseqConv ? toCE * ceDistRate : 0;
      const jbfYield = jbfBucket * ceDistRate + ceNewMoneyYield;

      const taxMethod = isYear1Conv ? year1TaxMethod : years2PlusTaxMethod;
      const surplus   = isYear1Conv ? 0 : Math.max(0, jbfYield - convTaxCost);

      if (engineSeedSource === 'existing_roth') {
        jbfYieldUsedForTax  = Math.min(jbfYield, convTaxCost);
        externalTaxThisYear = Math.max(0, convTaxCost - jbfYieldUsedForTax);
        if (isYear1Conv && externalTaxThisYear > 0) year1TopUpFromNQ = Math.round(externalTaxThisYear);
        jbfSurplusToRoth = surplus;
        newJBFSeed       = toCE;
        newStandardRoth  = toRoth + surplus;
      } else if (taxMethod === 'roth_funded') {
        rothTaxPaidThisYear = Math.min(existingRoth * rothDistributionRate, convTaxCost);
        externalTaxThisYear = Math.max(0, convTaxCost - rothTaxPaidThisYear);
        jbfSurplusToRoth    = isYear1Conv ? 0 : jbfYield;
        newJBFSeed          = toCE;
        newStandardRoth     = toRoth + (isYear1Conv ? 0 : jbfYield);
      } else if (taxMethod === 'jbf_distribution') {
        jbfYieldUsedForTax  = Math.min(jbfYield, convTaxCost);
        externalTaxThisYear = Math.max(0, convTaxCost - jbfYield);
        jbfSurplusToRoth    = surplus;
        newJBFSeed          = toCE;
        newStandardRoth     = toRoth + surplus;
      } else if (taxMethod === 'internal') {
        const net = Math.max(0, convAmount - convTaxCost);
        const ceShare   = convAmount > 0 ? toCE / convAmount : 0;
        const tfliShare = convAmount > 0 ? tfliAllocThisYear / convAmount : 0;
        jbfSurplusToRoth = isYear1Conv ? 0 : jbfYield;
        newJBFSeed       = net * ceShare;
        newTfliSeed      = net * tfliShare;   // withholding shrinks the TFLI seed pro-rata too
        newStandardRoth  = net * (1 - ceShare - tfliShare) + (isYear1Conv ? 0 : jbfYield);
      } else if (taxMethod === 'jbf_then_withhold') {
        // The engine covers what its distributions can; ONLY the shortfall is
        // withheld out of the conversion. Out of pocket is $0 in every year, and
        // unlike 'internal' the account keeps every dollar the engine can carry —
        // which, once the engine is at size, is the whole bill.
        jbfYieldUsedForTax  = Math.min(jbfYield, convTaxCost);
        const shortfall     = Math.max(0, convTaxCost - jbfYieldUsedForTax);
        const netAfterHold  = Math.max(0, convAmount - shortfall);
        jbfSurplusToRoth    = surplus;
        newJBFSeed          = netAfterHold * convCEShare;
        newTfliSeed         = netAfterHold * convTfliShare;
        newStandardRoth     = netAfterHold * (1 - convCEShare - convTfliShare) + surplus;
        externalTaxThisYear = 0;
      } else { // cash
        jbfSurplusToRoth    = isYear1Conv ? 0 : jbfYield;
        newJBFSeed          = toCE;
        newStandardRoth     = toRoth + (isYear1Conv ? 0 : jbfYield);
        externalTaxThisYear = convTaxCost;
      }
    }

    // ── Balance updates ─────────────────────────────────────────────────────────
    const baseROR            = isRetired ? postRetirementROR : preRetirementROR;
    // The un-converted traditional IRA grows at its own rate (defaults to the ROR).
    const iraGrowth          = unconvertedIraGrowthRate != null ? unconvertedIraGrowthRate : baseROR;
    const annualRothContrib  = !isRetired ? monthlyRothContribution  * 12 : 0;
    const annualOtherContrib = !isRetired ? monthlyOtherContribution * 12 : 0;

    // IRA: post-conv, post-iraRMD, post-QCD-beyond-RMD, then grow (gap draw handled below)
    const iraPostConvRMD = Math.max(0, ira - convAmount - iraRMD - qcdExtraFromIra);
    const iraEnd_preGap  = isRetired
      ? Math.max(0, iraPostConvRMD * (1 + iraGrowth))
      : Math.max(0, ira * (1 + iraGrowth) - convAmount - iraRMD); // main's iraGrowth + pre-ret RMD subtraction (conv+RMD netted once)

    const rothContribToJBF = (includeRothContribInJBF && isConvYear) ? annualRothContrib : 0;
    const rothContribToStd = annualRothContrib - rothContribToJBF;

    // ── Unified Year-1 CE coverage ───────────────────────────────────────────────
    // ONE calculation behind both the Year-1 external tax AND the "cost to start"
    // line, so they always reconcile. The full Year-1 CE seed (bucket after the
    // Year-1 top-up, capped at target) throws 11% for year1CEMonths of the year.
    // Applied identically for BOTH seed sources; only Year 1 is prorated (Year 2+
    // assume a full 12 months, since each year's top-up is done in January).
    if (isYear1Conv) {
      const y1months = Math.min(12, Math.max(1, year1CEMonths));
      const y1Rate   = ceDistRate * (y1months / 12);

      // 'jbf_then_withhold' in Year 1 is circular: whatever is withheld shrinks the
      // seed, which shrinks the distribution, which raises what still has to be
      // withheld. Solve it instead of assuming — each pass moves the answer by only
      // the distribution rate, so it settles in a few. Without this the engine gets
      // sized on a seed the withholding has already spent.
      if (year1TaxMethod === 'jbf_then_withhold') {
        let held = convTaxCost;
        for (let pass = 0; pass < 8; pass++) {
          const seed  = jbfBucket + Math.max(0, convAmount - held) * convCEShare + rothContribToJBF;
          const cover = Math.min(convTaxCost, seed * y1Rate);
          held = Math.max(0, convTaxCost - cover);
        }
        const netHeld = Math.max(0, convAmount - held);
        newJBFSeed    = netHeld * convCEShare;
        newTfliSeed   = netHeld * convTfliShare;
        newStandardRoth = netHeld * (1 - convCEShare - convTfliShare);
      }

      year1CESeed = jbfBucket + newJBFSeed + rothContribToJBF;
      const y1Distribution = year1CESeed * y1Rate;
      // 'internal' already withheld the whole conversion tax out of the conversion
      // itself (see the taxMethod branch above), so the Year-1 distribution is not
      // needed to cover it and must NOT be billed a second time. Charging it here
      // double-counted the tax and made withholding cost MORE out of pocket than
      // paying cash — the opposite of what the method exists for. Every other
      // method is untouched, so scenarios that do not select 'internal' are
      // byte-identical.
      const y1Withheld    = year1TaxMethod === 'internal';
      const y1Hybrid      = year1TaxMethod === 'jbf_then_withhold';
      const y1Coverage    = y1Withheld ? 0 : Math.min(convTaxCost, y1Distribution);
      jbfYieldUsedForTax  = Math.round(y1Coverage);
      // Both withholding methods take the tax out of the conversion, so neither may
      // bill it again here.
      externalTaxThisYear = (y1Withheld || y1Hybrid) ? 0 : Math.max(0, convTaxCost - y1Coverage);
      // Year-1 distribution NOT used for tax must spill to Standard Roth — never dropped.
      // (Previously Year 1 forced surplus to 0, silently losing this dollar.)
      const y1Surplus     = Math.max(0, y1Distribution - y1Coverage);
      jbfSurplusToRoth   += y1Surplus;
      newStandardRoth    += y1Surplus;
      if (engineSeedSource === 'existing_roth') year1TopUpFromNQ = Math.round(externalTaxThisYear);
    }

    totalExternalTaxPaid += externalTaxThisYear;
    totalJBFTaxFunded    += jbfYieldUsedForTax;

    // ── TFLI bucket income (tax-free, into cash-flow coverage) ──────────────────
    // January convention, matching the CE top-up: this year's TFLI allocation captures a
    // full year of yield in its own conversion year (Year 1 prorated by year1CEMonths,
    // same as the CE seed). Principal stays flat — the yield is fully distributed, for
    // life (keeps paying after conversions complete).
    const tfliMonthsFrac = isYear1Conv ? Math.min(12, Math.max(1, year1CEMonths)) / 12 : 1;
    const tfliIncome = tfliOn ? (tfliBucket + newTfliSeed) * tfliYield * tfliMonthsFrac : 0;

    // Merge buckets on first post-conversion year
    if (postConversion && jbfBucket > 0) {
      standardRoth += jbfBucket + existingRoth;
      jbfBucket    = 0;
      existingRoth = 0;
    }

    // The engine RETAINS its 3% and grows year over year (14/11/3 mechanic) — this is the
    // inflation buffer: a bucket that grows means its 11% distribution grows too, so a
    // rising conversion-tax bill stays covered. Only the 11% distribution NET OF TAX spills
    // to Standard Roth (handled in the conversion routing above + the Year-1 block below).
    // No cap-and-spill of the retention — same for existing-Roth and new-sleeve seeds.
    const jbfBucketEnd_preFee = (isYear1Conv || isSubseqConv)
      ? Math.max(0, (jbfBucket + newJBFSeed + rothContribToJBF) * (1 + ceRetention)) : 0;
    // Advisory fee — flat-by-tier on the end-of-year Bucket A balance, computed in arrears.
    // SELF-FUNDED assumption (Aug 12 2026): tiered engines are modeled FLAT — the planning
    // rate is the distribution only, while the sleeve's actual gross return is expected to
    // exceed it. That unmodeled spread self-funds the advisory fee, so on flat sleeves
    // (ceRetention === 0) the fee is REPORTED as a line item but NOT deducted from
    // principal — the bucket and its income hold flat instead of bleeding ~1%/yr.
    // Legacy 14/11/3 engines retain growth in-model, so the fee still nets against that
    // growth exactly as before (shipped no-tier plans stay byte-identical).
    const advisoryFee = advisoryFeeEnabled
      ? Math.round(jbfBucketEnd_preFee * advisoryFeeRate(jbfBucketEnd_preFee)) : 0;
    cumulativeAdvisoryFee += advisoryFee;
    const feeSelfFunded  = ceRetention === 0;
    const jbfBucketEnd   = feeSelfFunded
      ? jbfBucketEnd_preFee
      : Math.max(0, jbfBucketEnd_preFee - advisoryFee);
    const existingRothEnd = Math.max(0, existingRoth * (1 + riskROR) - rothTaxPaidThisYear);
    const nqEnd_preGap    = Math.max(0, nq * (1 + baseROR) + annualOtherContrib);
    const annBktEnd_preGap = Math.max(0, (annBkt - annuityRMD)) * (1 + annuityRate);

    if (postConversion && conversionCompleteRow == null)
      conversionCompleteRow = rows[rows.length - 1] ?? null;

    // ── Tax + gap solve ──────────────────────────────────────────────────────────
    let ssTaxable=0, grossTaxable=0, taxableIncome=0, seniorBonus=0;
    let stateTaxAmt=0, irmaa=0, totalTax=0;
    let rothDist=0, netIncome=0, incomeGap=0, taxableReinvestDelta=0;
    let nqGapDraw=0, annuityGapDraw=0, iraGapGross=0, fundingExhausted=false;
    let standardRothEnd, gapWithdrawal=0;
    // 2-year IRMAA lookback: premium-year MAGI comes from the row two years prior. Hoisted
    // to loop scope so the pre-retirement branch (working years with a Medicare-enrolled
    // spouse) can apply IRMAA too, not just the retired branches.
    const irmaaLag = yearFromNow >= 2 ? (magiHistory[yearFromNow - 2] ?? 0) : 0;

    if (isRetired) {

      if (postConversion) {
        // Post-conversion: use solveTaxAndGap on full income stack
        const solved = solveTaxAndGap({
          totalSSAnnual, pensionAnnual, rmdTotal: rmd, convTaxable: 0,
          rothBalance:    standardRoth,
          nqBalance:      nq,                 // pre-growth (subtract-then-grow)
          annuityBalance: annBkt,
          iraBalance:     iraPostConvRMD,
          filingStatus: effectiveFS, state, stateFlatRate,
          age, spouseAge: spouseCurrentAge,
          calendarYear, stdDed, irmaaLag,
          // TFLI income covers expenses first (tax-free, never enters the taxable stack);
          // the solver only has to fund the remainder. $0 TFLI ⇒ unchanged.
          annualExpenses: Math.max(0, annualExpenses - tfliIncome),
          allowIraDraw: true,                 // post-conversion: IRA drawn last-resort
          rowOrdinary, rowPreferential, rowNII,
          dependents,
        });
        rothDist             = solved.rothDist;
        nqGapDraw            = solved.nqGapDraw;
        annuityGapDraw       = solved.annuityGapDraw;
        iraGapGross          = solved.iraGapGross;
        fundingExhausted     = solved.fundingExhausted;
        netIncome            = solved.netIncome;
        incomeGap            = solved.incomeGap;
        taxableReinvestDelta = solved.taxableReinvestDelta;
        ssTaxable            = solved.ssTaxable;
        grossTaxable         = solved.grossTaxable;
        taxableIncome        = solved.taxableIncome;
        seniorBonus          = solved.seniorBonus;
        stateTaxAmt          = solved.stateTax;
        irmaa                = solved.irmaa;
        totalTax             = solved.totalTax;
        standardRothEnd      = Math.max(0, (standardRoth - rothDist) * (1 + riskROR));

        // Adjust balances for gap draws
        // (annBktEnd and nqEnd already at post-RMD; subtract gap draws)
        // These are applied below in final balance section
      } else {
        // Conversion years: full tax on SS+pension+RMD+conv; gap covered by chain
        // Include this year's conversion proceeds in available Roth for gap coverage
        const solved = solveTaxAndGap({
          totalSSAnnual, pensionAnnual, rmdTotal: rmd, convTaxable: convAmount,
          rothBalance:    standardRoth,        // fresh proceeds (newStandardRoth) NOT spendable this year
          nqBalance:      nq,                  // pre-growth (subtract-then-grow)
          annuityBalance: annBkt,
          iraBalance:     iraPostConvRMD,
          filingStatus: effectiveFS, state, stateFlatRate,
          age, spouseAge: spouseCurrentAge,
          calendarYear, stdDed, irmaaLag,
          // TFLI income covers expenses first (tax-free); solver funds the remainder.
          annualExpenses: Math.max(0, annualExpenses - tfliIncome),
          externalConvTax: externalTaxThisYear,
          allowIraDraw: false,                 // conversion window: preserve the IRA
          rowOrdinary, rowPreferential, rowNII,
          dependents,
        });
        rothDist             = solved.rothDist;
        nqGapDraw            = solved.nqGapDraw;
        annuityGapDraw       = solved.annuityGapDraw;
        iraGapGross          = solved.iraGapGross;
        fundingExhausted     = solved.fundingExhausted;
        netIncome            = solved.netIncome;
        incomeGap            = solved.incomeGap;
        taxableReinvestDelta = solved.taxableReinvestDelta;
        ssTaxable            = solved.ssTaxable;
        grossTaxable         = solved.grossTaxable;
        taxableIncome        = solved.taxableIncome;
        seniorBonus          = solved.seniorBonus;
        stateTaxAmt          = solved.stateTax;
        irmaa                = solved.irmaa;
        totalTax             = solved.totalTax;
        standardRothEnd      = Math.max(0, (standardRoth + newStandardRoth - rothDist) * (1 + riskROR) + rothContribToStd);
      }
    } else {
      // Pre-retirement: simple growth, no gap logic.
      // Earned income (W2/SE) stacks into ordinary taxable income during working years,
      // below conversions, held flat (no wage inflation). This branch is age < retirementAge
      // by construction, so earned income applies through the working years, then $0 once
      // age >= retirementAge (the retired branches above carry no earned income).
      const earnedIncome = age < retirementAge ? preRetirementIncome : 0;
      standardRothEnd = Math.max(0, standardRoth * (1 + riskROR) + newStandardRoth + rothContribToStd);
      // Earned income, pension, RMD, and typed ORDINARY rows stack as ordinary income (below
      // the conversion); PREFERENTIAL rows raise provisional + MAGI but tax at 15%. A forced
      // RMD stacks even in a working year — earned income never suppresses it.
      const preOrd = earnedIncome + pensionAnnual + rmd + rowOrdinary;
      const provWithConv = (preOrd + convAmount) + rowPreferential + 0.5 * totalSSAnnual;
      ssTaxable    = calcSSTaxable(provWithConv, totalSSAnnual, effectiveFS);
      seniorBonus  = calcSeniorBonusDeduction(ssTaxable + preOrd + convAmount + rowPreferential, age, spouseCurrentAge, effectiveFS, calendarYear);
      const grossOrd = ssTaxable + preOrd + convAmount;
      taxableIncome = Math.max(0, grossOrd - stdDed - seniorBonus);
      const fedRes = calcFederalTax(taxableIncome, effectiveFS, calendarYear);
      // Stage B: LTCG/QDI stacked 0/15/20 above ordinary taxable income, plus NIIT.
      const fedPref = preferentialFedTax(taxableIncome, rowPreferential, effectiveFS)
                    + niitTax(rowNII, grossOrd + rowPreferential, effectiveFS);
      grossTaxable = grossOrd + rowPreferential;
      // State ordinary base carries earned + ordinary/preferential rows + conversion + RMD.
      stateTaxAmt  = calcStateTax(preOrd + convAmount + rowPreferential, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual, calendarYear, dependents);
      // IRMAA applies whenever a spouse is Medicare-enrolled (age >= 65), even while still
      // working pre-retirement, using the 2-year-lagged MAGI. medicarePersons() = 0 below 65,
      // so this naturally yields $0 for pre-65 working years.
      irmaa        = calcIRMAA(irmaaLag, effectiveFS, medicarePersons(age, spouseCurrentAge), calendarYear);
      totalTax     = fedRes.tax + fedPref + stateTaxAmt + irmaa;

      // Conserve the forced distribution: a still-working client does not spend the RMD,
      // so its net-of-tax proceeds are reinvested into the taxable bucket (same place the
      // retired path parks unused RMD). Income rows appear in both bases so rmdTax isolates
      // only the RMD's marginal tax; the IRA tie-out is unchanged.
      if (rmd > 0) {
        const noRmdOrd   = earnedIncome + pensionAnnual + convAmount + rowOrdinary;
        const provNoRmd  = noRmdOrd + rowPreferential + 0.5 * totalSSAnnual;
        const ssTaxNoRmd = calcSSTaxable(provNoRmd, totalSSAnnual, effectiveFS);
        const bonusNoRmd = calcSeniorBonusDeduction(ssTaxNoRmd + noRmdOrd + rowPreferential, age, spouseCurrentAge, effectiveFS, calendarYear);
        const fedNoRmd   = calcFederalTax(Math.max(0, ssTaxNoRmd + noRmdOrd - stdDed - bonusNoRmd), effectiveFS, calendarYear).tax;
        const stateNoRmd = calcStateTax(noRmdOrd + rowPreferential, state, age, spouseCurrentAge, effectiveFS, stateFlatRate, pensionAnnual, calendarYear, dependents);
        const rmdTax     = Math.max(0, (fedRes.tax - fedNoRmd) + (stateTaxAmt - stateNoRmd));
        taxableReinvestDelta = Math.max(0, rmd - rmdTax);
      }
    }

    // ── TFLI income application ─────────────────────────────────────────────────
    // The used portion tops netIncome back up to TRUE expenses (the Expenses column
    // stays honest); any excess distribution spills to Standard Roth — tax-free money
    // is never dropped. Pre-retirement rows have no expense engine, so the whole
    // distribution spills. No-op when the feature is off (tfliIncome = 0).
    let tfliIncomeUsed = 0;
    if (tfliIncome > 0) {
      let tfliSurplusToRoth;
      if (isRetired) {
        tfliIncomeUsed    = Math.min(tfliIncome, annualExpenses);
        tfliSurplusToRoth = tfliIncome - tfliIncomeUsed;
        netIncome        += tfliIncomeUsed;
      } else {
        tfliSurplusToRoth = tfliIncome;
      }
      standardRothEnd += tfliSurplusToRoth;
    }

    // TFLI bucket end: flat principal (yield fully distributed). The advisory fee, when
    // enabled, is REPORTED for this managed sleeve (flat-by-tier, arrears) but — like the
    // tiered CE above — is SELF-FUNDED by gross growth above the planning distribution
    // rate, so it is never deducted from principal and never erodes the TFLI income.
    const tfliBucketEnd = tfliOn ? Math.max(0, tfliBucket + newTfliSeed) : 0;
    const tfliAdvFee = (advisoryFeeEnabled && tfliBucketEnd > 0)
      ? Math.round(tfliBucketEnd * advisoryFeeRate(tfliBucketEnd)) : 0;
    cumulativeAdvisoryFee += tfliAdvFee;

    // Final balance adjustments for gap draws
    // Subtract-then-grow, identical to the baseline engine. Retired rows subtract
    // the voluntary IRA gap draw BEFORE growth; pre-retirement has no gap draw
    // (iraGapGross is 0 there) so its growth path is untouched.
    const iraFinal   = isRetired
      ? Math.max(0, iraPostConvRMD - iraGapGross) * (1 + iraGrowth)
      : Math.max(0, iraEnd_preGap);
    const annBktFinal = Math.max(0, (annBkt - annuityRMD - annuityGapDraw)) * (1 + annuityRate);
    const nqFinal    = Math.max(0, nq - nqGapDraw) * (1 + baseROR) + annualOtherContrib;

    if (_snBumps[age]) standardRothEnd += _snBumps[age]; // SN note matures tax-free into Roth
    const combinedRoth = jbfBucketEnd + standardRothEnd + existingRothEnd + tfliBucketEnd;
    // IRMAA MAGI ≈ AGI: taxable SS + earned + pension + RMD + conversion + taxable gap draws.
    // grossTaxable already carries taxable SS and earned income (NOT the SS-excluded state
    // base) and excludes gap draws, so add the gap draws back here. Do not use full SS.
    const thisMagi     = grossTaxable + iraGapGross + annuityGapDraw;

    const trGainJBF    = taxableReinvest * baseROR;
    taxableReinvest    = Math.max(0, taxableReinvest * (1 + baseROR) - trGainJBF * 0.15 + taxableReinvestDelta);

    rows.push({
      age, isRetired, filingStatus: effectiveFS,
      conversionComplete:  postConversion,
      iraBalanceStart:     Math.round(ira),
      iraBalance:          Math.round(iraFinal),
      rothBalance:         Math.round(combinedRoth),
      nonQualBalance:      Math.round(nqFinal),
      annuityBalance:      Math.round(annBktFinal),
      conversionAmount:    Math.round(convAmount),
      snMaturityPayoff:    Math.round(_snBumps[age] || 0),
      conversionToRoth:    Math.round(newJBFSeed + newStandardRoth + newTfliSeed),
      // Top federal bracket the LAST conversion dollar lands in (conversion is the top of
      // the ordinary stack). 0 in non-conversion years → table renders "—".
      convMarginalRate:    convAmount > 0 ? getMarginalRate(taxableIncome, calendarYear, effectiveFS) : 0,
      rmd:                 Math.round(rmdGross),
      qcd:                 Math.round(qcd),
      ssIncome:            Math.round(totalSSAnnual),
      pensionIncome:       Math.round(pensionAnnual),
      otherIncome:         Math.round(rowOrdinary + rowPreferential),
      // Total income by source = SS + pension + taxable RMD + conversion + other income.
      // (QCD is excluded — it goes to charity, not to her; Roth/gap draws shown separately.)
      totalIncome:         Math.round(totalSSAnnual + pensionAnnual + rmd + convAmount + rowOrdinary + rowPreferential),
      grossIncome:         Math.round(totalSSAnnual + pensionAnnual + rmd + convAmount + rowOrdinary + rowPreferential),
      rothDistribution:    Math.round(rothDist),
      ssTaxablePct:        totalSSAnnual > 0 ? Math.round((ssTaxable / totalSSAnnual) * 100) : 0,
      grossTaxableIncome:  Math.round(grossTaxable),
      standardDeduction:   Math.round(stdDed),
      seniorBonusDeduction:Math.round(seniorBonus),
      taxableIncome:       Math.round(taxableIncome),
      fedTax:              Math.round(totalTax - stateTaxAmt - irmaa),
      stateTax:            Math.round(stateTaxAmt),
      irmaa:               Math.round(irmaa),
      totalTax:            Math.round(totalTax),
      netIncome:           Math.round(netIncome),
      annualExpenses:      Math.round(annualExpenses),
      incomeGap:           Math.round(incomeGap),
      fundingExhausted:    !!fundingExhausted,
      iraGapGross:         Math.round(iraGapGross),
      nqGapDraw:           Math.round(nqGapDraw),
      annuityGapDraw:      Math.round(annuityGapDraw),
      taxableReinvestBalance: Math.round(taxableReinvest),
      legacy:              Math.round(afterTaxInheritedIra(iraFinal, calendarYear, heirOpts(iraGrowth)) + combinedRoth + nqFinal + taxableReinvest + annBktFinal * 0.75),
      iraDeferredTax:      Math.round(iraFinal * inheritedIraEffRate(iraFinal, calendarYear, heirOpts(iraGrowth))),
      jbfBucketBalance:    Math.round(jbfBucketEnd),
      standardRothBalance: Math.round(standardRothEnd + existingRothEnd),
      jbfYieldUsedForTax:  Math.round(jbfYieldUsedForTax),
      ceNewMoneyYield:     Math.round(ceNewMoneyYield),
      jbfSurplusToRoth:    Math.round(jbfSurplusToRoth),
      externalTaxPaid:     Math.round(externalTaxThisYear),
      conversionTaxCost:   Math.round(convTaxCost),
      rothTaxPaid:         Math.round(rothTaxPaidThisYear),
      advisoryFee:         advisoryFee + tfliAdvFee,
      cumulativeAdvisoryFee: Math.round(cumulativeAdvisoryFee),
      // TFLI bucket (0s when the feature is off — legacy rows unchanged numerically)
      tfliAlloc:           Math.round(tfliAllocThisYear),
      tfliBucketBalance:   Math.round(tfliBucketEnd),
      tfliIncome:          Math.round(tfliIncome),
      tfliIncomeUsed:      Math.round(tfliIncomeUsed),
    });

    ira          = iraFinal;
    jbfBucket    = jbfBucketEnd;
    standardRoth = standardRothEnd;
    existingRoth = existingRothEnd;
    nq           = nqFinal;
    annBkt       = annBktFinal;
    tfliBucket   = tfliBucketEnd;
    magiHistory.push(thisMagi);
  }

  // SAFETY: the CE target must throw enough (11% of it) to cover the PEAK conversion-tax
  // year, not just Year 1. If ceTarget × 11% < peak tax, the initial engine underfunds the
  // peak (retained growth may still catch up, but flag it so the advisor can size up).
  const ceMinTargetForPeak = peakConvTaxCost > 0 ? Math.ceil(peakConvTaxCost / ceDistRate) : 0;
  const ceTargetCoversPeak = ceTarget <= 0 ? null : (ceTarget >= ceMinTargetForPeak);
  const cePeakTargetShortfall = ceTarget <= 0 ? 0 : Math.max(0, ceMinTargetForPeak - ceTarget);
  return {
    rows, totalExternalTaxPaid, totalJBFTaxFunded, conversionCompleteRow, year1TopUpFromNQ,
    ceFullyFundedAge, year1CESeed,
    peakConvTaxCost: Math.round(peakConvTaxCost),
    ceMinTargetForPeak, ceTargetCoversPeak, cePeakTargetShortfall,
  };
}

// ─── Build params helpers ─────────────────────────────────────────────────────

export function buildJBFTwoBucketParams(form, scenario, { clientAge, spouseAge }) {
  return {
    clientAge,
    spouseAge:            form.includeSpouse && spouseAge != null ? spouseAge : null,
    retirementAge:        parseFloat(form.retirementAge) || (form.currentlyRetired ? clientAge : 65),
    lifeExpectancy:       parseFloat(form.lifeExpectancy) || 90,
    filingStatus:         form.filingStatus || 'mfj',
    state:                form.state || '',
    stateFlatRate:        parseFloat(form.stateFlatRate) || 0,
    dependents:           parseInt(form.dependents, 10) || 0,
    iraBalance:           (parseFloat(form.balance401k) || 0) + (parseFloat(form.balanceIRA) || 0),
    existingRoth:         parseFloat(form.balanceRothIRA) || 0,
    nonQualBalance:       parseFloat(form.balanceOther) || 0,
    monthlyContribution:  parseFloat(form.monthlyContribution) || 0,
    preRetirementROR:     (parseFloat(form.growthRate) || 7) / 100,
    postRetirementROR:    (parseFloat(form.growthRate) || 7) / 100,
    ssMonthly:            parseFloat(form.clientSSMonthly) || 0,
    // Per-scenario claiming age overrides the global; basis age (what the entered
    // benefit is stated at) defaults to the global claiming age for old cases.
    ssClaimingAge:        parseFloat(scenario?.ssClaimingAge) || parseFloat(form.clientSSClaimingAge) || 67,
    ssBasisAge:           parseFloat(form.clientSSBasisAge) || parseFloat(form.clientSSClaimingAge) || 67,
    spouseSSMonthly:      parseFloat(form.spouseSSMonthly) || 0,
    spouseSSClaimingAge:  parseFloat(scenario?.spouseSSClaimingAge) || parseFloat(form.spouseSSClaimingAge) || 67,
    spouseSSBasisAge:     parseFloat(form.spouseSSBasisAge) || parseFloat(form.spouseSSClaimingAge) || 67,
    pensionMonthly:       parseFloat(form.pensionMonthly) || 0,
    ssCOLARate:           (parseFloat(form.ssCOLARate) || 1.8) / 100,
    monthlyExpenses:      parseFloat(form.monthlyExpenses) || 0,
    inflationRate:        (parseFloat(form.inflationRate) || 2.75) / 100,
    survivorEnabled:      form.survivorEnabled || false,
    survivorAge:               parseFloat(form.survivorAge) || 80,
    // Typed income rows own earned income when present; the legacy preRetirementIncome
    // field is suppressed then (it was migrated into an earned row) to avoid double count.
    preRetirementIncome:       Array.isArray(form.incomeRows) ? 0 : (parseFloat(form.preRetirementIncome) || 0),
    incomeRows:                resolveIncomeRows(form),
    monthlyRothContribution:   parseFloat(form.monthlyRothContribution) || 0,
    monthlyOtherContribution:  parseFloat(form.monthlyOtherContribution) || 0,
    year1Amount:              parseFloat(scenario.year1Amount) || parseFloat(scenario.conversionAmount) || 0,
    year1ConversionAmount:    parseFloat(scenario.year1ConversionAmount) || parseFloat(scenario.conversionAmount) || 0,
    annualAmount:             parseFloat(scenario.annualAmount) || parseFloat(scenario.conversionAmount) || 0,
    startYear:            parseFloat(scenario.startYear) || 1,
    endYear:              parseFloat(scenario.endYear) || 1,
    // Flexible per-year schedule (source of truth). Falls back to the two-tier
    // preset for saved scenarios that predate the array.
    conversionSchedule:   resolveConversionSchedule(scenario, clientAge),
    year1TaxMethod:       scenario.year1TaxMethod || 'cash',
    years2PlusTaxMethod:  scenario.years2PlusTaxMethod || 'jbf_distribution',
    ceTargetSize:         parseFloat(scenario.ceTargetSize) || 0,
    rothDistributionRate: (parseFloat(scenario.rothDistributionRate) || 11) / 100,
    riskTier:             scenario.riskTier || 'M',
    qcdAnnual:            parseFloat(scenario.qcdAnnual) || 0,
    qcdStartAge:          (scenario.qcdStartAge != null && scenario.qcdStartAge !== '') ? parseFloat(scenario.qcdStartAge) : null,
    qcdOffsetRmd:         !!scenario.qcdOffsetRmd,
    includeRothContribInJBF: scenario.includeRothContribInJBF || false,
    engineSeedSource:        scenario.engineSeedSource || 'nonqual',
    // TFLI Bucket (per-scenario third sleeve) — off/empty for legacy scenarios.
    tfliEnabled:          !!scenario.tfliEnabled,
    tfliSchedule:         Array.isArray(scenario.tfliSchedule) ? scenario.tfliSchedule : null,
    tfliYield:            (parseFloat(scenario.tfliYield) || 8) / 100,
    annuityBalance: parseFloat(form.annuityBalance) || 0,
    annuityROR: (parseFloat(form.annuityROR) || 4) / 100,
    // Inherited-IRA haircut — heir defaults to client's state; UI override is a later task.
    heirBeneficiaryIncome: parseFloat(form.heirBeneficiaryIncome) || 100000,
    birthYear:       form.clientDOB ? new Date(form.clientDOB).getFullYear() : null,
    spouseBirthYear: form.includeSpouse && form.spouseDOB ? new Date(form.spouseDOB).getFullYear() : null,
    // Per-scenario un-converted IRA growth (percent → decimal). Undefined ⇒ engine uses the ROR.
    unconvertedIraGrowthRate: (scenario && scenario.unconvertedIraGrowthRate != null && scenario.unconvertedIraGrowthRate !== '')
      ? parseFloat(scenario.unconvertedIraGrowthRate) / 100 : undefined,
    // P1.1 SN maturity payoff (Regime B) — pass through raw; helper handles defaults/units.
    snPayoff:          scenario?.snPayoff,
    snBaseRate:        scenario?.snBaseRate,
    snTenor:           scenario?.snTenor,
    snMatLag:          scenario?.snMatLag,
    snNoteStartOffset: scenario?.snNoteStartOffset,
    heirState:             form.heirState || form.state || '',
    heirStateFlatRate:     parseFloat(form.heirStateFlatRate) || parseFloat(form.stateFlatRate) || 0,
    // Year-1 CE dividend months (drives the reconciled Year-1 external tax + cost-to-start).
    year1CEMonths:         Math.min(12, Math.max(1, parseInt(scenario?.year1CEMonths ?? form?.year1CEMonths, 10) || 12)),
    // CE advisory fee (Bucket A, flat-by-tier, in arrears). Off by default → Fox stays byte-identical.
    advisoryFeeEnabled:    !!form.showAdvisoryFee,
    // CE portfolio tier (Aug 2026). When the scenario carries a valid tier, the engine
    // runs Option A: distribution = planningRate, retention = 0 (flat engine principal).
    // The roth_funded tax method draws the existing Roth at the same tier rate. Scenarios
    // without a tier omit these keys entirely → legacy 14/11/3 defaults, byte-identical.
    ...(CE_TIERS[scenario?.tier] ? {
      ceDistRate:           CE_TIERS[scenario.tier].planningRate,
      ceRetention:          0,
      rothDistributionRate: CE_TIERS[scenario.tier].planningRate,
    } : {}),
  };
}

export function buildV2Params(form, scenario, { clientAge, spouseAge, rothROR } = {}) {
  const vRates = scenario
    ? vehicleRates(scenario.vehicle, scenario.ror)
    : { grossYield: 0.07, distributionRate: 0, netRetention: 0.07 };
  // Commit 1: the pre-conversion (existing) Roth must compound on the SAME basis in the
  // baseline as in the Convert Engine scenario, which grows it at the scenario's Roth ROR
  // (riskROR). When building the baseline (scenario == null) grow it at that same rothROR
  // instead of a hardcoded 7%. No-op when rothROR is 7% (e.g. risk tier M), so those cases
  // stay byte-identical. Only affects the baseline's existing Roth — not the CE seed model.
  const existingRothRetention = (scenario == null && rothROR != null) ? rothROR : vRates.netRetention;
  // Explicit per-year conversion schedule (custom amounts) — passed through to the
  // engine for ALL vehicles, exactly like the Convert Engine. Only materialized when
  // the scenario actually carries a custom array; otherwise null, so the flat
  // annualAmount + start/end window path is used unchanged (byte-identical for legacy
  // scenarios that never touched the per-year grid).
  const _explicitSchedule = (scenario && Array.isArray(scenario.conversionSchedule) && scenario.conversionSchedule.length)
    ? resolveConversionSchedule(scenario, clientAge)
    : null;
  const _scheduleTotal = _explicitSchedule
    ? _explicitSchedule.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0) : 0;
  const _hasConv = !!scenario && (parseFloat(scenario.conversionAmount) > 0 || _scheduleTotal > 0);
  return {
    clientAge,
    spouseAge: form.includeSpouse && spouseAge != null ? spouseAge : null,
    retirementAge:  parseFloat(form.retirementAge) || (form.currentlyRetired ? clientAge : 65),
    lifeExpectancy: parseFloat(form.lifeExpectancy) || 90,
    filingStatus:   form.filingStatus || 'mfj',
    state:          form.state || '',
    stateFlatRate:  parseFloat(form.stateFlatRate) || 0,
    dependents:     parseInt(form.dependents, 10) || 0,
    iraBalance:     (parseFloat(form.balance401k) || 0) + (parseFloat(form.balanceIRA) || 0),
    rothBalance:    parseFloat(form.balanceRothIRA) || 0,
    nonQualBalance: parseFloat(form.balanceOther) || 0,
    monthlyContribution:  parseFloat(form.monthlyContribution) || 0,
    preRetirementROR:     (parseFloat(form.growthRate) || 7) / 100,
    postRetirementROR:    (parseFloat(form.growthRate) || 7) / 100,
    rothNetRetention:     existingRothRetention,
    rothDistributionRate: vRates.distributionRate,
    rothGrossPreRetirement: vRates.grossYield || 0.14,
    ssMonthly:            parseFloat(form.clientSSMonthly) || 0,
    // Per-scenario claiming age overrides the global; basis age (what the entered
    // benefit is stated at) defaults to the global claiming age for old cases.
    ssClaimingAge:        parseFloat(scenario?.ssClaimingAge) || parseFloat(form.clientSSClaimingAge) || 67,
    ssBasisAge:           parseFloat(form.clientSSBasisAge) || parseFloat(form.clientSSClaimingAge) || 67,
    spouseSSMonthly:      parseFloat(form.spouseSSMonthly) || 0,
    spouseSSClaimingAge:  parseFloat(scenario?.spouseSSClaimingAge) || parseFloat(form.spouseSSClaimingAge) || 67,
    spouseSSBasisAge:     parseFloat(form.spouseSSBasisAge) || parseFloat(form.spouseSSClaimingAge) || 67,
    pensionMonthly:       parseFloat(form.pensionMonthly) || 0,
    ssCOLARate:           (parseFloat(form.ssCOLARate) || 1.8) / 100,
    monthlyExpenses:      parseFloat(form.monthlyExpenses) || 0,
    inflationRate:        (parseFloat(form.inflationRate) || 2.75) / 100,
    conversions: _hasConv ? {
      startYear:   parseFloat(scenario.startYear) || 1,
      endYear:     parseFloat(scenario.endYear) || 1,
      annualAmount: parseFloat(scenario.conversionAmount) || 0,
      taxMethod:   scenario.taxMethod || 'cash',
      vehicle:     scenario.vehicle || 'standard_roth',
      schedule:    _explicitSchedule,
      annuityGrowthRate: scenario.taxMethod === 'annuity_bonus'
        ? (parseFloat(scenario.annuityGrowthRate) || 4) / 100 : undefined,
      annuityBonusPct: scenario.taxMethod === 'annuity_bonus'
        ? (parseFloat(scenario.annuityBonusPct) || 18) / 100 : undefined,
    } : null,
    survivorEnabled:      form.survivorEnabled || false,
    survivorAge:          parseFloat(form.survivorAge) || 80,
    // Typed income rows own earned income when present; the legacy preRetirementIncome
    // field is suppressed then (it was migrated into an earned row) to avoid double count.
    preRetirementIncome:      Array.isArray(form.incomeRows) ? 0 : (parseFloat(form.preRetirementIncome) || 0),
    incomeRows:               resolveIncomeRows(form),
    monthlyRothContribution:  parseFloat(form.monthlyRothContribution) || 0,
    monthlyOtherContribution: parseFloat(form.monthlyOtherContribution) || 0,
    annuityBalance: parseFloat(form.annuityBalance) || 0,
    annuityROR:     (parseFloat(form.annuityROR) || 4) / 100,
    // Inherited-IRA haircut — heir defaults to client's state; UI override is a later task.
    heirBeneficiaryIncome: parseFloat(form.heirBeneficiaryIncome) || 100000,
    birthYear:       form.clientDOB ? new Date(form.clientDOB).getFullYear() : null,
    spouseBirthYear: form.includeSpouse && form.spouseDOB ? new Date(form.spouseDOB).getFullYear() : null,
    // Per-scenario un-converted IRA growth (percent → decimal). Undefined ⇒ engine uses the ROR.
    unconvertedIraGrowthRate: (scenario && scenario.unconvertedIraGrowthRate != null && scenario.unconvertedIraGrowthRate !== '')
      ? parseFloat(scenario.unconvertedIraGrowthRate) / 100 : undefined,
    heirState:             form.heirState || form.state || '',
    heirStateFlatRate:     parseFloat(form.heirStateFlatRate) || parseFloat(form.stateFlatRate) || 0,
    qcdAnnual:   scenario ? (parseFloat(scenario.qcdAnnual) || 0) : 0,
    qcdStartAge: (scenario && scenario.qcdStartAge != null && scenario.qcdStartAge !== '') ? parseFloat(scenario.qcdStartAge) : null,
    qcdOffsetRmd: scenario ? !!scenario.qcdOffsetRmd : false,
    // Year-1 CE dividend months (drives the reconciled Year-1 external tax + cost-to-start).
    year1CEMonths:         Math.min(12, Math.max(1, parseInt(scenario?.year1CEMonths ?? form?.year1CEMonths, 10) || 12)),
  };
}